/**
 * FlareSearch Widget (Single-Tenant)
 * Embeddable search widget for WooCommerce stores
 *
 * Usage:
 * <script src="https://your-worker.workers.dev/widget.js"></script>
 * <script>
 *   FlareSearch.init({
 *     inputSelector: '#search-input',
 *     mode: 'dropdown' // or 'fullscreen'
 *   });
 * </script>
 */

import { styles, fullscreenStyles } from './styles'

export interface FlareSearchConfig {
  inputSelector?: string
  apiBaseUrl?: string
  mode?: 'dropdown' | 'fullscreen' | 'trigger'
  theme?: {
    primaryColor?: string
    backgroundColor?: string
    textColor?: string
    borderRadius?: string
  }
  debounceMs?: number
  minChars?: number
  maxResults?: number
  popularSearches?: string[]
  // URL for a logo image displayed top-left inside the fullscreen header.
  // If unset, the default magnifying-glass icon is shown.
  logoUrl?: string
  // Tax rate to apply to displayed prices. 0 = no calc (shows raw price).
  // 10 = Australian GST (price * 1.10 + " inc GST" label).
  taxRatePercent?: number
  // Label shown after the tax-adjusted price. Only rendered when taxRatePercent > 0.
  taxLabel?: string
  // Trigger-mode options. Only used when mode === 'trigger'.
  trigger?: {
    /** CSS selector to inject the trigger inside (appendChild). If absent, trigger is fixed-positioned in the viewport. */
    insertInto?: string
    /** Where to place a fixed-positioned trigger when insertInto is unset. */
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
    /** Visible label shown next to the magnifying-glass icon. Empty string = icon only. */
    label?: string
    /** Hide the trigger below this min-width media query (px). Default: never hide. */
    hideBelowPx?: number
  }
}

interface ThemeConfig {
  primaryColor: string
  backgroundColor: string
  textColor: string
  borderRadius: string
}

interface InternalConfig {
  inputSelector: string
  apiBaseUrl: string
  mode: 'dropdown' | 'fullscreen' | 'trigger'
  theme: ThemeConfig
  debounceMs: number
  minChars: number
  maxResults: number
  popularSearches: string[]
  logoUrl: string | null
  taxRatePercent: number
  taxLabel: string
  trigger: {
    insertInto: string | null
    position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
    label: string
    hideBelowPx: number | null
  }
}

type ResultType = 'product' | 'post' | 'page' | 'category' | 'brand' | 'fitment'

interface SearchResult {
  id: string
  type: ResultType
  sourceId: string
  title: string
  subtitle?: string | null
  body?: string | null
  url: string | null
  imageUrl: string | null
  // Product-only fields (null for other types)
  sku?: string | null
  price?: number | null
  salePrice?: number | null
  inStock?: boolean | null
  brand?: string | null
  category?: string | null
  meta?: Record<string, unknown> | null
  matchType?: 'fts' | 'trigram' | 'both' | 'sku'
}

interface ProductDetails {
  id: string
  sku: string | null
  title: string
  description: string | null
  price: number | null
  salePrice: number | null
  category: string | null
  categories: string[] | Array<{ name: string; slug: string }>
  brand?: string | null
  brandSlug?: string | null
  brands?: string[]
  imageUrl: string | null
  url: string | null
  inStock: boolean
  addToCartUrl: string | null
}

interface SimilarProduct {
  id: string
  title: string
  price: number | null
  salePrice: number | null
  imageUrl: string | null
  url: string | null
  category: string | null
  inStock: boolean
  source: string
  score: number
}

interface AutocompleteResponse {
  results: SearchResult[]
  query: string
  latencyMs: number
  facetCounts?: {
    brand: Record<string, number>
    category: Record<string, number>
    model: Record<string, number>
  }
}

interface StoreInfo {
  store: {
    name: string
    logoUrl: string | null
    productCount: number
    lastSync: string | null
  }
  categories: Array<{
    id: string
    name: string
    slug: string
    productCount: number
    imageUrl: string | null
  }>
  brands: Array<{
    id: string
    name: string
    slug: string
    productCount: number
    imageUrl: string | null
  }>
  models?: Array<{
    id: string
    make: string
    name: string
    slug: string
    fitmentCount: number
  }>
  typeCounts: Record<string, number>
  popularSearches: string[]
  topProducts?: Array<{
    id: string
    title: string
    sku: string | null
    imageUrl: string | null
    price: number | null
    salePrice: number | null
    inStock: number | null
    url: string | null
    brand: string | null
  }>
}

class FlareSearchWidget {
  private config: InternalConfig
  private container: HTMLDivElement | null = null
  private shadowRoot: ShadowRoot | null = null
  // Trigger button container — separate from `container` because the
  // trigger persists across openFullscreen/closeFullscreen cycles, while
  // `container` is recreated and removed each time the overlay opens.
  private triggerContainer: HTMLDivElement | null = null
  private input: HTMLInputElement | null = null
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private lastQuery: string = ''
  private isOpen: boolean = false
  private originalInput: HTMLInputElement | null = null
  private storeInfo: StoreInfo | null = null
  private sessionId: string
  private quickViewContainer: HTMLDivElement | null = null
  private recentSearches: string[] = []
  private static RECENT_SEARCHES_KEY = 'flaresearch_recent'
  private static MAX_RECENT_SEARCHES = 10

  // Active facet filters (reset on widget close)
  private filterCategory: string | null = null
  private filterBrand: string | null = null
  private filterType: ResultType | null = null
  private filterPriceMin: number | null = null
  private filterPriceMax: number | null = null
  private filterInStockOnly: boolean = false
  // Sort order for results. 'relevance' = default (BM25).
  private sortOrder: 'relevance' | 'price_asc' | 'price_desc' | 'newest' = 'relevance'
  // Keyboard handler reference — stored so closeFullscreen can detach it
  // cleanly regardless of how the overlay was closed (Esc, click, etc).
  private activeKeyHandler: ((e: KeyboardEvent) => void) | null = null
  // popstate handler — back button closes overlay on mobile (no Esc key).
  // Set during openFullscreen, removed during closeFullscreen.
  private activePopstateHandler: ((e: PopStateEvent) => void) | null = null
  // Whether we pushed a history entry on open (so we know to pop it on
  // programmatic close — e.g. when the user taps the X instead of back).
  private didPushHistory: boolean = false

  // Live facet counts from the most recent search response. The brand/
  // category chip rendering reads these to show live counts.
  private liveFacetCounts: { brand: Record<string, number>; category: Record<string, number>; model: Record<string, number> } = {
    brand: {},
    category: {},
    model: {},
  }

  // Active fitment-model filter (id like "model:norton-commando-750-...").
  private filterModelId: string | null = null

  // View stack for in-search navigation. The base view is always 'search'.
  // Pushing a product view shows the product detail overlay; popping returns
  // to the previous view (search results stay intact underneath).
  private viewStack: Array<
    | { type: 'search' }
    | { type: 'product'; productId: string }
    | { type: 'fitment'; fitmentId: string }
  > = [{ type: 'search' }]

  constructor(config: FlareSearchConfig) {
    this.config = {
      inputSelector:
        config.inputSelector || 'input[type="search"], input[name="s"], .search-field',
      apiBaseUrl: config.apiBaseUrl || window.location.origin,
      mode: config.mode || 'dropdown',
      theme: {
        primaryColor: config.theme?.primaryColor || '#2563eb',
        backgroundColor: config.theme?.backgroundColor || '#ffffff',
        textColor: config.theme?.textColor || '#1f2937',
        borderRadius: config.theme?.borderRadius || '8px',
      },
      debounceMs: config.debounceMs || 150,
      minChars: config.minChars || 2,
      maxResults: config.maxResults || 8,
      popularSearches: config.popularSearches || [],
      logoUrl: config.logoUrl || null,
      taxRatePercent: typeof config.taxRatePercent === 'number' ? config.taxRatePercent : 0,
      taxLabel: config.taxLabel || 'inc GST',
      trigger: {
        insertInto: config.trigger?.insertInto || null,
        position: config.trigger?.position || 'top-right',
        label: config.trigger?.label ?? 'Search',
        hideBelowPx: config.trigger?.hideBelowPx ?? null,
      },
    }

    // Initialize or retrieve session ID for tracking
    this.sessionId = this.getOrCreateSessionId()
  }

  /**
   * Get or create a persistent session ID for tracking
   * Stored in sessionStorage to persist across page navigations
   */
  private getOrCreateSessionId(): string {
    const storageKey = 'flaresearch_session_id'
    let sessionId = sessionStorage.getItem(storageKey)

    if (!sessionId) {
      // Generate a unique session ID
      sessionId = 'fs_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36)
      sessionStorage.setItem(storageKey, sessionId)
    }

    return sessionId
  }

  /**
   * Load recent searches from localStorage
   */
  private loadRecentSearches(): void {
    try {
      const stored = localStorage.getItem(FlareSearchWidget.RECENT_SEARCHES_KEY)
      this.recentSearches = stored ? JSON.parse(stored) : []
    } catch {
      this.recentSearches = []
    }
  }

  /**
   * Save a search query to recent searches
   */
  private saveRecentSearch(query: string): void {
    if (!query || query.length < 2) return

    // Remove if already exists, add to front
    this.recentSearches = [
      query,
      ...this.recentSearches.filter(q => q.toLowerCase() !== query.toLowerCase())
    ].slice(0, FlareSearchWidget.MAX_RECENT_SEARCHES)

    try {
      localStorage.setItem(FlareSearchWidget.RECENT_SEARCHES_KEY, JSON.stringify(this.recentSearches))
    } catch {
      // localStorage might be full or disabled
    }
  }

  /**
   * Enter in the search box commits the query to recent searches. Typing
   * alone does not (issue #14: "wiring h", "wiring ha" ... were each saved).
   */
  private handleSearchKeydown(e: KeyboardEvent): void {
    if (e.key !== 'Enter') return
    const query = ((e.target as HTMLInputElement | null)?.value ?? '').trim()
    this.saveRecentSearch(query)
  }

  /**
   * Clear all recent searches
   */
  private clearRecentSearches(): void {
    this.recentSearches = []
    try {
      localStorage.removeItem(FlareSearchWidget.RECENT_SEARCHES_KEY)
    } catch {
      // Ignore
    }
  }

  /**
   * Remove a single term from recent searches (persists to localStorage).
   */
  private removeRecentSearch(term: string): void {
    this.recentSearches = this.recentSearches.filter(
      (t) => t.toLowerCase() !== term.toLowerCase()
    )
    try {
      localStorage.setItem(
        FlareSearchWidget.RECENT_SEARCHES_KEY,
        JSON.stringify(this.recentSearches)
      )
    } catch {
      // Ignore
    }
  }

  /**
   * Render the Doofinder-style recent-search chip bar that sits directly
   * below the search input. Each chip is clickable (re-runs that search)
   * and has an X button to remove it individually.
   */
  private renderRecentChips(): void {
    if (!this.shadowRoot) return
    const bar = this.shadowRoot.querySelector('.fs-recent-bar') as HTMLElement | null
    const chipsContainer = this.shadowRoot.querySelector('.fs-recent-chips')
    if (!bar || !chipsContainer) return

    if (this.recentSearches.length === 0) {
      bar.style.display = 'none'
      chipsContainer.textContent = ''
      return
    }

    bar.style.display = ''
    chipsContainer.textContent = ''
    for (const term of this.recentSearches.slice(0, 10)) {
      const chip = document.createElement('span')
      chip.className = 'fs-recent-chip'

      const label = document.createElement('button')
      label.type = 'button'
      label.className = 'fs-recent-chip-label'
      label.textContent = term
      label.addEventListener('click', () => {
        if (this.input) {
          this.input.value = term
          this.handleInput(term)
        }
      })

      const close = document.createElement('button')
      close.type = 'button'
      close.className = 'fs-recent-chip-close'
      close.setAttribute('aria-label', `Remove ${term}`)
      close.textContent = '×'
      close.addEventListener('click', (e) => {
        e.stopPropagation()
        this.removeRecentSearch(term)
        this.renderRecentChips()
      })

      chip.appendChild(label)
      chip.appendChild(close)
      chipsContainer.appendChild(chip)
    }
  }

  // ============================================
  // DOOFINDER-STYLE LAYOUT HELPERS
  // ============================================

  /**
   * Render suggestion chips from a list of query strings below the
   * search input (Doofinder behaviour: when typing, the recent-search
   * bar is replaced by related query chips). Non-removable; clicking
   * fills the input + runs that search.
   */
  private renderSuggestChips(suggestions: string[]): void {
    if (!this.shadowRoot) return
    const bar = this.shadowRoot.querySelector('.fs-suggest-bar') as HTMLElement | null
    const container = this.shadowRoot.querySelector('.fs-suggest-chips') as HTMLElement | null
    if (!bar || !container) return

    if (suggestions.length === 0) {
      bar.style.display = 'none'
      container.textContent = ''
      return
    }

    bar.style.display = ''
    container.textContent = ''
    for (const term of suggestions) {
      const chip = document.createElement('button')
      chip.type = 'button'
      chip.className = 'fs-suggest-chip'
      chip.textContent = term
      chip.addEventListener('click', () => {
        if (this.input) {
          this.input.value = term
          this.handleInput(term)
        }
      })
      container.appendChild(chip)
    }
  }

  /**
   * Debounced suggestion fetch. Called from handleInput whenever query
   * changes. Uses a separate timer to the main search debounce so
   * suggestions can fire at a slightly different cadence (faster is fine
   * since they're lightweight).
   */
  private suggestDebounce: ReturnType<typeof setTimeout> | null = null
  private async fetchSuggestions(query: string): Promise<void> {
    if (this.suggestDebounce) clearTimeout(this.suggestDebounce)
    this.suggestDebounce = setTimeout(async () => {
      try {
        const response = await fetch(
          `${this.config.apiBaseUrl}/api/search/suggest?q=${encodeURIComponent(query)}`
        )
        if (!response.ok) return
        const data = await response.json() as { suggestions?: string[] }
        this.renderSuggestChips(data.suggestions ?? [])
      } catch {
        // Silent fail — suggestions are non-critical UI sugar
      }
    }, 120)
  }

  /**
   * Swap between recent-search chips (empty input) and suggestion chips
   * (typing). Called on every keystroke.
   */
  private syncChipBars(query: string): void {
    if (!this.shadowRoot) return
    const recentBar = this.shadowRoot.querySelector('.fs-recent-bar') as HTMLElement | null
    if (query.length === 0) {
      this.renderSuggestChips([]) // hide suggestions
      this.renderRecentChips() // show recent if any
    } else {
      if (recentBar) recentBar.style.display = 'none'
      void this.fetchSuggestions(query)
    }
  }

  /**
   * Render the Popular Searches column (initial state, left).
   * Plain vertical text-link list, lowercase, no decoration — matches
   * Doofinder's reference layout.
   */
  private renderPopularList(terms: string[]): void {
    if (!this.shadowRoot) return
    const ul = this.shadowRoot.querySelector('.fs-popular-list') as HTMLUListElement | null
    const column = this.shadowRoot.querySelector('.fs-popular-column') as HTMLElement | null
    if (!ul || !column) return
    if (terms.length === 0) {
      column.style.display = 'none'
      return
    }
    column.style.display = ''
    ul.textContent = ''
    for (const term of terms.slice(0, 12)) {
      const li = document.createElement('li')
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'fs-popular-link'
      btn.textContent = term
      btn.addEventListener('click', () => {
        if (this.input) {
          this.input.value = term
          this.handleInput(term)
        }
      })
      li.appendChild(btn)
      ul.appendChild(li)
    }
  }

  /**
   * Render the Recommended Products horizontal scroll rail (initial state, right).
   * Pulls from storeInfo.topProducts which the server populates from
   * 30-day clickthrough data (with newest-product fallback).
   */
  private renderRecommendedRail(): void {
    if (!this.shadowRoot) return
    const track = this.shadowRoot.querySelector('.fs-rail-track') as HTMLElement | null
    const section = this.shadowRoot.querySelector('.fs-recommended-section') as HTMLElement | null
    if (!track || !section) return

    const products = this.storeInfo?.topProducts ?? []
    if (products.length === 0) {
      section.style.display = 'none'
      return
    }
    section.style.display = ''
    track.textContent = ''

    for (const p of products) {
      // Reuse existing renderResultCard by adapting the topProducts shape
      // to a SearchResult — `inStock` is a number (0/1) on the API but
      // SearchResult expects boolean | null.
      const asResult: SearchResult = {
        id: `product:${p.id}`,
        type: 'product',
        sourceId: p.id,
        title: p.title,
        subtitle: p.brand ?? null,
        body: null,
        url: p.url,
        imageUrl: p.imageUrl,
        sku: p.sku,
        price: p.price,
        salePrice: p.salePrice,
        inStock: p.inStock === null ? null : p.inStock === 1,
        brand: p.brand,
        category: null,
      }
      const cardHtml = this.renderResultCard(asResult, 'fullscreen')
      const wrapper = document.createElement('div')
      wrapper.className = 'fs-rail-item'
      wrapper.innerHTML = cardHtml
      track.appendChild(wrapper)
    }

    // Attach click tracking — rail cards are <a> tags that navigate to the
    // product page, but we still want analytics to record the click.
    this.attachResultClickHandlers(track)

    // Wire scroll arrows
    const leftBtn = this.shadowRoot.querySelector('.fs-rail-arrow-left') as HTMLButtonElement | null
    const rightBtn = this.shadowRoot.querySelector('.fs-rail-arrow-right') as HTMLButtonElement | null
    const updateArrows = () => {
      if (!leftBtn || !rightBtn) return
      leftBtn.style.opacity = track.scrollLeft <= 4 ? '0.3' : '1'
      const maxScroll = track.scrollWidth - track.clientWidth
      rightBtn.style.opacity = track.scrollLeft >= maxScroll - 4 ? '0.3' : '1'
    }
    leftBtn?.addEventListener('click', () => {
      track.scrollBy({ left: -track.clientWidth * 0.8, behavior: 'smooth' })
    })
    rightBtn?.addEventListener('click', () => {
      track.scrollBy({ left: track.clientWidth * 0.8, behavior: 'smooth' })
    })
    track.addEventListener('scroll', updateArrows, { passive: true })
    setTimeout(updateArrows, 50) // initial state after layout
  }

  /**
   * Storage for facet term lists so View More / per-facet search can
   * re-render without losing the source data.
   */
  private facetTermsState: {
    categories?: { id: string; label: string; count: number }[]
    brands?: { id: string; label: string; count: number }[]
  } = {}
  private facetExpanded: Record<string, boolean> = { categories: false, brands: false }
  private facetSearchFilter: Record<string, string> = { categories: '', brands: '' }
  private static FACET_INITIAL_LIMIT = 5

  /**
   * Populate a facet's term list (Categories or Brand). Stores the source
   * list in `facetTermsState` so View More + per-facet search filter can
   * re-render against the original data without another network roundtrip.
   */
  private renderFacetTerms(
    facetName: 'categories' | 'brands',
    terms: { id: string; label: string; count: number }[]
  ): void {
    if (!this.shadowRoot) return
    this.facetTermsState[facetName] = terms
    this.repaintFacet(facetName)
  }

  private repaintFacet(facetName: 'categories' | 'brands'): void {
    if (!this.shadowRoot) return
    const ul = this.shadowRoot.querySelector(`.fs-facet-terms[data-facet="${facetName}"]`) as HTMLElement | null
    if (!ul) return

    const terms = this.facetTermsState[facetName] ?? []
    const filter = (this.facetSearchFilter[facetName] || '').toLowerCase()
    const matching = filter
      ? terms.filter((t) => t.label.toLowerCase().includes(filter))
      : terms

    // Selected term pins to the top — matches Doofinder behaviour so the
    // user always sees which facet is currently active without scrolling.
    // Compare by label (we store the filter value as a name; see below).
    const activeLabel = facetName === 'categories' ? this.filterCategory : this.filterBrand
    const visible = activeLabel
      ? [
          ...matching.filter((t) => t.label === activeLabel),
          ...matching.filter((t) => t.label !== activeLabel),
        ]
      : matching

    const expanded = this.facetExpanded[facetName] ?? false
    const slice = expanded
      ? visible
      : visible.slice(0, FlareSearchWidget.FACET_INITIAL_LIMIT)

    ul.textContent = ''
    for (const term of slice) {
      const li = document.createElement('li')
      li.className = 'fs-facet-term'

      const label = document.createElement('label')
      label.className = 'fs-facet-term-label'

      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      checkbox.className = 'fs-facet-term-checkbox'
      checkbox.value = term.id
      checkbox.dataset['facet'] = facetName
      // Reflect current filter state. The server-side filter matches
      // against the category/brand NAME (p.category / p.brand in the
      // products table), so we store and compare using the label, not
      // the opaque id.
      if (facetName === 'categories' && this.filterCategory === term.label) checkbox.checked = true
      if (facetName === 'brands' && this.filterBrand === term.label) checkbox.checked = true
      checkbox.addEventListener('change', () => {
        if (facetName === 'categories') {
          this.filterCategory = checkbox.checked ? term.label : null
          // Uncheck other category checkboxes (single-select for now)
          ul.querySelectorAll('.fs-facet-term-checkbox').forEach((cb) => {
            const c = cb as HTMLInputElement
            if (c !== checkbox) c.checked = false
          })
        } else if (facetName === 'brands') {
          this.filterBrand = checkbox.checked ? term.label : null
          ul.querySelectorAll('.fs-facet-term-checkbox').forEach((cb) => {
            const c = cb as HTMLInputElement
            if (c !== checkbox) c.checked = false
          })
        }
        const facet = facetName === 'categories' ? 'category' : 'brand'
        const value = checkbox.checked ? term.label : null
        void this.trackEvent('filter', undefined, undefined, { facet, value })
        this.rerunSearch()
      })

      const name = document.createElement('span')
      name.className = 'fs-facet-term-name'
      name.textContent = term.label

      const count = document.createElement('span')
      count.className = 'fs-facet-term-count'
      // Live count overlay: prefer the most recent search's facet counts
      const liveCount = facetName === 'categories'
        ? this.liveFacetCounts.category[term.label]
        : this.liveFacetCounts.brand[term.label]
      count.textContent = String(liveCount ?? term.count ?? '')

      label.appendChild(checkbox)
      label.appendChild(name)
      label.appendChild(count)
      li.appendChild(label)
      ul.appendChild(li)
    }

    // Footer: count summary + View more / Show less
    const facetEl = this.shadowRoot.querySelector(`.fs-facet[data-facet="${facetName}"]`) as HTMLElement | null
    const countLabel = facetEl?.querySelector('.fs-facet-count-label') as HTMLElement | null
    const moreBtn = facetEl?.querySelector('.fs-facet-more') as HTMLButtonElement | null
    if (countLabel) {
      const total = visible.length
      const shownCount = slice.length
      countLabel.textContent = total > FlareSearchWidget.FACET_INITIAL_LIMIT
        ? `Showing ${shownCount} of ${total} terms`
        : ''
    }
    if (moreBtn) {
      const hasMore = visible.length > FlareSearchWidget.FACET_INITIAL_LIMIT
      moreBtn.style.display = hasMore ? '' : 'none'
      moreBtn.textContent = expanded ? 'Show less' : 'View more'
    }
  }

  /**
   * Wire facet UI: accordion toggles, per-facet search inputs, View more buttons.
   * Idempotent — only attaches listeners once per facet element.
   */
  private wireFacetInteractions(): void {
    if (!this.shadowRoot) return
    const facets = this.shadowRoot.querySelectorAll('.fs-facet')
    facets.forEach((facet) => {
      const facetName = (facet as HTMLElement).dataset['facet']
      if (!facetName) return

      // Toggle accordion
      const toggle = facet.querySelector('.fs-facet-toggle') as HTMLButtonElement | null
      const body = facet.querySelector('.fs-facet-body') as HTMLElement | null
      toggle?.addEventListener('click', () => {
        const isOpen = toggle.getAttribute('aria-expanded') !== 'false'
        toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true')
        if (body) body.style.display = isOpen ? 'none' : ''
      })

      // Per-facet term-search filter
      const facetSearchInput = facet.querySelector('.fs-facet-search') as HTMLInputElement | null
      facetSearchInput?.addEventListener('input', () => {
        if (facetName === 'categories' || facetName === 'brands') {
          this.facetSearchFilter[facetName] = facetSearchInput.value.trim()
          this.repaintFacet(facetName)
        }
      })

      // View more / Show less
      const moreBtn = facet.querySelector('.fs-facet-more') as HTMLButtonElement | null
      moreBtn?.addEventListener('click', () => {
        if (facetName === 'categories' || facetName === 'brands') {
          this.facetExpanded[facetName] = !this.facetExpanded[facetName]
          this.repaintFacet(facetName)
        }
      })
    })
  }

  /**
   * Switch the body between initial-state (popular + recommended) and
   * results-state (facets + grid) based on whether there's an active query.
   * Called from handleInput on every keystroke.
   */
  private setBodyState(state: 'initial' | 'results' | 'loading' | 'empty'): void {
    if (!this.shadowRoot) return
    const initialBody = this.shadowRoot.querySelector('.fs-initial-body') as HTMLElement | null
    const resultsBody = this.shadowRoot.querySelector('.fs-results-body') as HTMLElement | null
    const loading = this.shadowRoot.querySelector('.fs-loading') as HTMLElement | null
    const empty = this.shadowRoot.querySelector('.fs-empty') as HTMLElement | null
    const grid = this.shadowRoot.querySelector('.fs-results-grid') as HTMLElement | null

    if (state === 'initial') {
      if (initialBody) initialBody.style.display = ''
      if (resultsBody) resultsBody.style.display = 'none'
    } else {
      if (initialBody) initialBody.style.display = 'none'
      if (resultsBody) resultsBody.style.display = ''
      if (loading) loading.style.display = state === 'loading' ? '' : 'none'
      if (empty) empty.style.display = state === 'empty' ? '' : 'none'
      if (grid) grid.style.display = state === 'results' ? '' : 'none'
    }
  }

  init(): void {
    // Trigger mode is self-sufficient — it does NOT require an existing
    // input on the page; instead it injects its own search button.
    // We still try to attach to a configured input (if found) so mobile
    // themes that have a visible search input get the same fullscreen UX.
    if (this.config.mode === 'trigger') {
      this.originalInput = document.querySelector(this.config.inputSelector)
      this.loadRecentSearches()
      this.fetchStoreInfo()
      this.initTrigger()
      // If a real input is also visible on the page (e.g. mobile theme),
      // wire fullscreen up to it as well so taps on it open the overlay.
      if (this.originalInput) {
        this.initFullscreen()
      }
      console.log('FlareSearch: Initialized, mode: trigger')
      return
    }

    // dropdown / fullscreen modes both require an input to attach to.
    this.originalInput = document.querySelector(this.config.inputSelector)

    if (!this.originalInput) {
      console.warn(`FlareSearch: No input found matching "${this.config.inputSelector}"`)
      return
    }

    // Load recent searches from localStorage
    this.loadRecentSearches()

    // Fetch store info in background
    this.fetchStoreInfo()

    if (this.config.mode === 'fullscreen') {
      this.initFullscreen()
    } else {
      this.initDropdown()
    }

    console.log('FlareSearch: Initialized, mode:', this.config.mode)
  }

  // ============================================
  // TRIGGER MODE — self-sufficient search button
  // ============================================
  private initTrigger(): void {
    // Build the trigger button host (separate shadow DOM so theme styles
    // don't leak in or out).
    this.triggerContainer = document.createElement('div')
    this.triggerContainer.id = 'flaresearch-trigger'
    const isFixed = !this.config.trigger.insertInto

    const shadow = this.triggerContainer.attachShadow({ mode: 'open' })
    const styleEl = document.createElement('style')
    styleEl.textContent = this.getTriggerStyles(isFixed)
    shadow.appendChild(styleEl)

    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'fs-trigger-btn'
    button.setAttribute('aria-label', 'Open search')

    // Build SVG icon via DOM (no innerHTML = no XSS surface).
    const SVG_NS = 'http://www.w3.org/2000/svg'
    const svg = document.createElementNS(SVG_NS, 'svg')
    svg.setAttribute('class', 'fs-trigger-icon')
    svg.setAttribute('viewBox', '0 0 24 24')
    svg.setAttribute('fill', 'none')
    svg.setAttribute('stroke', 'currentColor')
    svg.setAttribute('stroke-width', '2.5')
    svg.setAttribute('stroke-linecap', 'round')
    svg.setAttribute('stroke-linejoin', 'round')
    svg.setAttribute('aria-hidden', 'true')
    const circle = document.createElementNS(SVG_NS, 'circle')
    circle.setAttribute('cx', '11')
    circle.setAttribute('cy', '11')
    circle.setAttribute('r', '8')
    const line = document.createElementNS(SVG_NS, 'path')
    line.setAttribute('d', 'm21 21-4.35-4.35')
    svg.appendChild(circle)
    svg.appendChild(line)
    button.appendChild(svg)

    // Label via textContent — no HTML interpretation, fully safe with any user-supplied string.
    if (this.config.trigger.label) {
      const labelSpan = document.createElement('span')
      labelSpan.className = 'fs-trigger-label'
      labelSpan.textContent = this.config.trigger.label
      button.appendChild(labelSpan)
    }

    button.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.openFullscreen()
    })
    shadow.appendChild(button)

    // Inject into the configured target, or fix-position to viewport.
    let mounted = false
    if (this.config.trigger.insertInto) {
      const target = document.querySelector(this.config.trigger.insertInto)
      if (target) {
        target.appendChild(this.triggerContainer)
        mounted = true
      } else {
        console.warn(
          `FlareSearch trigger: insertInto target "${this.config.trigger.insertInto}" not found, falling back to fixed position`
        )
      }
    }
    if (!mounted) {
      document.body.appendChild(this.triggerContainer)
    }
  }

  private getTriggerStyles(isFixed: boolean): string {
    const { primaryColor } = this.config.theme
    const positionCss = isFixed ? this.getTriggerPositionCss() : ''
    const hideQuery =
      this.config.trigger.hideBelowPx != null
        ? `@media (max-width: ${this.config.trigger.hideBelowPx - 1}px) { :host { display: none !important; } }`
        : ''
    return `
      :host {
        ${isFixed ? 'position: fixed;' : 'position: relative;'}
        ${positionCss}
        z-index: 999998;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      }
      ${hideQuery}
      .fs-trigger-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 16px;
        background: ${primaryColor};
        color: #ffffff;
        border: none;
        border-radius: 999px;
        font-size: 14px;
        font-weight: 600;
        line-height: 1;
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        transition: transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease;
        -webkit-tap-highlight-color: transparent;
      }
      .fs-trigger-btn:hover {
        filter: brightness(1.08);
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        transform: translateY(-1px);
      }
      .fs-trigger-btn:active { transform: translateY(0); }
      .fs-trigger-icon { width: 16px; height: 16px; flex-shrink: 0; }
      .fs-trigger-label { white-space: nowrap; }
    `
  }

  private getTriggerPositionCss(): string {
    switch (this.config.trigger.position) {
      case 'top-left':     return 'top: 16px; left: 16px;'
      case 'bottom-right': return 'bottom: 16px; right: 16px;'
      case 'bottom-left':  return 'bottom: 16px; left: 16px;'
      case 'top-right':
      default:             return 'top: 16px; right: 16px;'
    }
  }

  /**
   * Fetch store info (categories, popular searches, product count)
   */
  private async fetchStoreInfo(): Promise<void> {
    try {
      const response = await fetch(`${this.config.apiBaseUrl}/api/store/info`)

      if (response.ok) {
        this.storeInfo = await response.json()
        console.log('FlareSearch: Store info loaded', this.storeInfo)

        // Update popular searches from analytics if available
        if (this.storeInfo?.popularSearches?.length) {
          this.config.popularSearches = this.storeInfo.popularSearches
        }
      }
    } catch (error) {
      console.warn('FlareSearch: Failed to fetch store info', error)
    }
  }

  // ============================================
  // DROPDOWN MODE
  // ============================================
  private initDropdown(): void {
    this.createDropdownContainer()
    this.attachDropdownEventListeners()
  }

  private createDropdownContainer(): void {
    // Create container element
    this.container = document.createElement('div')
    this.container.id = 'flaresearch-container'
    this.container.style.cssText = 'position: absolute; z-index: 999999;'

    // Create shadow root for style isolation
    this.shadowRoot = this.container.attachShadow({ mode: 'open' })

    // Inject styles
    const styleEl = document.createElement('style')
    styleEl.textContent = this.getStyles()
    this.shadowRoot.appendChild(styleEl)

    // Create dropdown
    const dropdown = document.createElement('div')
    dropdown.className = 'flaresearch-dropdown'
    dropdown.innerHTML = `
			<div class="flaresearch-results"></div>
			<div class="flaresearch-loading" style="display: none;">
				<div class="flaresearch-spinner"></div>
				<span>Searching...</span>
			</div>
			<div class="flaresearch-empty" style="display: none;">
				No results found
			</div>
		`
    this.shadowRoot.appendChild(dropdown)

    // Add to document body (not relative to input)
    document.body.appendChild(this.container)

    // Position dropdown
    this.positionDropdown()
  }

  private getStyles(): string {
    const { primaryColor, backgroundColor, textColor, borderRadius } = this.config.theme

    return styles
      .replace(/var\(--fs-primary\)/g, primaryColor)
      .replace(/var\(--fs-bg\)/g, backgroundColor)
      .replace(/var\(--fs-text\)/g, textColor)
      .replace(/var\(--fs-radius\)/g, borderRadius)
  }

  private positionDropdown(): void {
    if (!this.originalInput || !this.container) return

    const inputRect = this.originalInput.getBoundingClientRect()
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft

    this.container.style.top = `${inputRect.bottom + scrollTop + 4}px`
    this.container.style.left = `${inputRect.left + scrollLeft}px`
    this.container.style.width = `${Math.max(inputRect.width, 350)}px`
  }

  private attachDropdownEventListeners(): void {
    if (!this.originalInput) return

    // Input event for search
    this.originalInput.addEventListener('input', (e) => {
      const query = (e.target as HTMLInputElement).value.trim()
      this.handleInput(query)
    })
    this.originalInput.addEventListener('keydown', (e) => this.handleSearchKeydown(e))

    // Focus event - show recent searches if no query
    this.originalInput.addEventListener('focus', () => {
      const currentValue = this.originalInput?.value.trim() || ''
      if (currentValue.length >= this.config.minChars) {
        this.showDropdown()
      } else if (this.recentSearches.length > 0) {
        this.showRecentSearches()
      }
    })

    // Click outside to close
    document.addEventListener('click', (e) => {
      const target = e.target as Node
      if (!this.container?.contains(target) && target !== this.originalInput) {
        this.hideDropdown()
      }
    })

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideDropdown()
      }
    })

    // Reposition on scroll/resize
    window.addEventListener('scroll', () => this.positionDropdown(), { passive: true })
    window.addEventListener('resize', () => this.positionDropdown(), { passive: true })
  }

  /**
   * Show recent searches in the dropdown (when focusing with empty input)
   */
  private showRecentSearches(): void {
    const resultsEl = this.shadowRoot?.querySelector('.flaresearch-results')
    if (!resultsEl) return

    this.hideLoading()
    this.hideEmpty()

    const recentHtml = this.recentSearches.slice(0, 5).map(term => `
      <button class="flaresearch-recent-item" data-query="${term}">
        <svg class="flaresearch-recent-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        <span>${term}</span>
      </button>
    `).join('')

    resultsEl.innerHTML = `
      <div class="flaresearch-recent-header">
        <span>Recent Searches</span>
        <button class="flaresearch-clear-recent">Clear</button>
      </div>
      ${recentHtml}
    `

    // Add click handlers for recent items
    resultsEl.querySelectorAll('.flaresearch-recent-item').forEach(item => {
      item.addEventListener('click', () => {
        const query = (item as HTMLElement).dataset['query']
        if (query && this.originalInput) {
          this.originalInput.value = query
          this.handleInput(query)
        }
      })
    })

    // Add clear handler
    const clearBtn = resultsEl.querySelector('.flaresearch-clear-recent')
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        this.clearRecentSearches()
        this.hideDropdown()
      })
    }

    this.showDropdown()
  }

  // ============================================
  // FULLSCREEN MODE
  // ============================================
  private initFullscreen(): void {
    // Attach to ALL elements matching the selector — supports multi-selector
    // strings like '#desktop-search-btn, input[name="s"]' so a single config
    // can cover both a desktop button and a mobile input simultaneously.
    const targets = document.querySelectorAll(this.config.inputSelector)
    targets.forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault()
        this.openFullscreen()
      })
      el.addEventListener('focus', (e) => {
        e.preventDefault()
        this.openFullscreen()
      })
    })
  }

  private openFullscreen(): void {
    if (this.isOpen) return

    // Create fullscreen overlay
    this.container = document.createElement('div')
    this.container.id = 'flaresearch-fullscreen'

    // Create shadow root
    this.shadowRoot = this.container.attachShadow({ mode: 'open' })

    // Inject styles
    const styleEl = document.createElement('style')
    styleEl.textContent = this.getFullscreenStyles()
    this.shadowRoot.appendChild(styleEl)

    // Get store info for display
    const productCount = this.storeInfo?.store?.productCount || 0
    const categories = this.storeInfo?.categories || []
    const popularSearches = this.storeInfo?.popularSearches?.length
      ? this.storeInfo.popularSearches
      : this.config.popularSearches

    // Create fullscreen UI with new layout
    const overlay = document.createElement('div')
    overlay.className = 'fs-overlay'
    overlay.setAttribute('role', 'dialog')
    overlay.setAttribute('aria-modal', 'true')
    overlay.setAttribute('aria-label', 'Product search')
    const logoHtml = this.config.logoUrl
      ? `<a class="fs-logo" href="/" aria-label="Home"><img src="${this.escapeHtml(this.config.logoUrl)}" alt="Logo"></a>`
      : `<svg class="fs-search-icon-default" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>`
    overlay.innerHTML = `
			<div class="fs-header" role="search">
				<div class="fs-search-container">
					${logoHtml}
					<input type="text" class="fs-search-input" placeholder="Search ${productCount > 0 ? productCount.toLocaleString() : ''}${productCount > 0 ? ' products' : 'products'}..." autocomplete="off" autofocus aria-label="Search products" role="searchbox">
					<button class="fs-close-btn" type="button" aria-label="Close search">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
							<path d="M18 6L6 18M6 6l12 12"></path>
						</svg>
					</button>
				</div>
				<div class="fs-recent-bar" style="display:none">
					<span class="fs-recent-label">Latest searches:</span>
					<div class="fs-recent-chips"></div>
					<button class="fs-recent-clear" type="button">Delete all</button>
				</div>
				<div class="fs-suggest-bar" style="display:none">
					<div class="fs-suggest-chips"></div>
				</div>
			</div>
			<div class="fs-body">
				<div class="fs-sidebar-backdrop" aria-hidden="true"></div>

				<!-- INITIAL STATE: empty input -->
				<div class="fs-initial-body">
					<aside class="fs-popular-column">
						<h3 class="fs-uppercase-title">Popular Searches</h3>
						<ul class="fs-popular-list"></ul>
					</aside>
					<section class="fs-recommended-section">
						<h3 class="fs-uppercase-title">Recommended Products</h3>
						<div class="fs-rail-wrapper">
							<button class="fs-rail-arrow fs-rail-arrow-left" type="button" aria-label="Scroll left">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
							</button>
							<button class="fs-rail-arrow fs-rail-arrow-right" type="button" aria-label="Scroll right">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
							</button>
							<div class="fs-rail-track"></div>
						</div>
					</section>
				</div>

				<!-- RESULTS STATE: query active -->
				<div class="fs-results-body" style="display:none">
					<aside class="fs-facets-panel">
						<button class="fs-mobile-facets-close" type="button" aria-label="Close filters">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
						</button>

						<div class="fs-facet" data-facet="instock">
							<label class="fs-instock-toggle">
								<input type="checkbox" class="fs-filter-instock">
								<span>In stock only</span>
							</label>
						</div>

						<div class="fs-facet" data-facet="categories">
							<button class="fs-facet-toggle" type="button" aria-expanded="true">
								<h3 class="fs-uppercase-title">Categories</h3>
								<svg class="fs-facet-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="18 15 12 9 6 15"/></svg>
							</button>
							<div class="fs-facet-body">
								<div class="fs-facet-search-wrap">
									<svg class="fs-facet-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
									<input class="fs-facet-search" placeholder="Search options" type="text">
								</div>
								<ul class="fs-facet-terms" data-facet="categories"></ul>
								<div class="fs-facet-footer">
									<span class="fs-facet-count-label"></span>
									<button class="fs-facet-more fs-action-link" type="button">View more</button>
								</div>
							</div>
						</div>

						<div class="fs-facet" data-facet="brands">
							<button class="fs-facet-toggle" type="button" aria-expanded="true">
								<h3 class="fs-uppercase-title">Brand</h3>
								<svg class="fs-facet-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="18 15 12 9 6 15"/></svg>
							</button>
							<div class="fs-facet-body">
								<div class="fs-facet-search-wrap">
									<svg class="fs-facet-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
									<input class="fs-facet-search" placeholder="Search options" type="text">
								</div>
								<ul class="fs-facet-terms" data-facet="brands"></ul>
								<div class="fs-facet-footer">
									<span class="fs-facet-count-label"></span>
									<button class="fs-facet-more fs-action-link" type="button">View more</button>
								</div>
							</div>
						</div>

						<div class="fs-facet" data-facet="price">
							<button class="fs-facet-toggle" type="button" aria-expanded="true">
								<h3 class="fs-uppercase-title">Price</h3>
								<svg class="fs-facet-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="18 15 12 9 6 15"/></svg>
							</button>
							<div class="fs-facet-body">
								<div class="fs-filter-price-row">
									<input type="number" class="fs-filter-price-min" placeholder="Min" min="0" step="1">
									<span>–</span>
									<input type="number" class="fs-filter-price-max" placeholder="Max" min="0" step="1">
								</div>
							</div>
						</div>

						<button class="fs-filters-clear fs-action-link" type="button">Clear all</button>
					</aside>

					<section class="fs-results-section">
						<div class="fs-results-header">
							<button class="fs-mobile-facets-btn" type="button" aria-label="Open filters">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/></svg>
								<span>Filters</span>
							</button>
							<span class="fs-results-count" aria-live="polite" aria-atomic="true"><strong>0</strong> results found</span>
							<label class="fs-sort-wrap">
								<span class="fs-sort-label">Sort by:</span>
								<select class="fs-sort-select">
									<option value="relevance">Relevance</option>
									<option value="price_asc">Price: Low to high</option>
									<option value="price_desc">Price: High to low</option>
									<option value="newest">Newest</option>
								</select>
							</label>
						</div>
						<div class="fs-results-grid"></div>
						<div class="fs-loading" role="status" aria-live="polite" style="display: none;">
							<div class="fs-spinner" aria-hidden="true"></div>
							<span>Searching...</span>
						</div>
						<div class="fs-empty" role="status" aria-live="polite" style="display: none;">
							<p>No results found</p>
							<p class="fs-hint">Try a different search term</p>
						</div>
					</section>
				</div>

				<!-- Product detail view: slides over results when a card is opened in-overlay.
				     Used only when openProductInOverlay is on; otherwise card click navigates. -->
				<div class="fs-product-view" style="display: none;"></div>
				<div class="fs-fitment-view" style="display: none;"></div>
			</div>
		`
    this.shadowRoot.appendChild(overlay)

    // Add to body
    document.body.appendChild(this.container)
    document.body.style.overflow = 'hidden'

    // Get references
    this.input = this.shadowRoot.querySelector('.fs-search-input')
    const closeBtn = this.shadowRoot.querySelector('.fs-close-btn')

    // ─────────────────────────────────────────────────────────────────
    // INITIAL STATE: popular searches list + recommended products rail
    // ─────────────────────────────────────────────────────────────────
    this.renderPopularList(popularSearches)
    this.renderRecommendedRail()

    // ─────────────────────────────────────────────────────────────────
    // RESULTS STATE: facet accordions
    // ─────────────────────────────────────────────────────────────────

    // Categories facet
    const brands = this.storeInfo?.brands ?? []
    if (categories.length > 0) {
      this.renderFacetTerms('categories', categories.map((c) => ({
        id: c.id,
        label: c.name,
        count: c.productCount,
      })))
    } else {
      const facet = this.shadowRoot.querySelector('.fs-facet[data-facet="categories"]') as HTMLElement | null
      if (facet) facet.style.display = 'none'
    }

    // Brand facet
    if (brands.length > 0) {
      this.renderFacetTerms('brands', brands.map((b) => ({
        id: b.id,
        label: b.name,
        count: b.productCount,
      })))
    } else {
      const facet = this.shadowRoot.querySelector('.fs-facet[data-facet="brands"]') as HTMLElement | null
      if (facet) facet.style.display = 'none'
    }

    // Wire facet accordion toggles + per-facet search inputs + View more buttons
    this.wireFacetInteractions()

    // Wire In-stock toggle
    const inStockCheckbox = this.shadowRoot.querySelector('.fs-filter-instock') as HTMLInputElement | null
    inStockCheckbox?.addEventListener('change', () => {
      this.filterInStockOnly = inStockCheckbox.checked
      void this.trackEvent('filter', undefined, undefined, { facet: 'inStock', value: inStockCheckbox.checked })
      this.rerunSearch()
    })

    // Wire sort dropdown
    const sortSelect = this.shadowRoot.querySelector('.fs-sort-select') as HTMLSelectElement | null
    sortSelect?.addEventListener('change', () => {
      this.sortOrder = (sortSelect.value as typeof this.sortOrder) || 'relevance'
      void this.trackEvent('filter', undefined, undefined, { facet: 'sort', value: this.sortOrder })
      this.rerunSearch()
    })

    // Wire price range inputs (debounced so typing doesn't hammer the API)
    const priceMinInput = this.shadowRoot.querySelector('.fs-filter-price-min') as HTMLInputElement | null
    const priceMaxInput = this.shadowRoot.querySelector('.fs-filter-price-max') as HTMLInputElement | null
    let priceDebounce: ReturnType<typeof setTimeout> | null = null
    const priceChanged = () => {
      if (priceDebounce) clearTimeout(priceDebounce)
      priceDebounce = setTimeout(() => {
        const min = priceMinInput?.value ? parseFloat(priceMinInput.value) : null
        const max = priceMaxInput?.value ? parseFloat(priceMaxInput.value) : null
        this.filterPriceMin = min !== null && !isNaN(min) ? min : null
        this.filterPriceMax = max !== null && !isNaN(max) ? max : null
        // Only fire one event per settled change (post-debounce), not per keystroke
        void this.trackEvent('filter', undefined, undefined, {
          facet: 'price',
          value: { min: this.filterPriceMin, max: this.filterPriceMax },
        })
        this.rerunSearch()
      }, 300)
    }
    priceMinInput?.addEventListener('input', priceChanged)
    priceMaxInput?.addEventListener('input', priceChanged)

    // Wire Clear-all button
    const clearBtn = this.shadowRoot.querySelector('.fs-filters-clear')
    clearBtn?.addEventListener('click', () => {
      this.filterCategory = null
      this.filterBrand = null
      this.filterModelId = null
      this.filterType = null
      this.filterPriceMin = null
      this.filterPriceMax = null
      this.filterInStockOnly = false
      this.sortOrder = 'relevance'
      if (inStockCheckbox) inStockCheckbox.checked = false
      if (priceMinInput) priceMinInput.value = ''
      if (priceMaxInput) priceMaxInput.value = ''
      if (sortSelect) sortSelect.value = 'relevance'
      // Re-render facet term lists to drop active checkbox state
      if (categories.length > 0) {
        this.renderFacetTerms('categories', categories.map((c) => ({
          id: c.id, label: c.name, count: c.productCount,
        })))
      }
      if (brands.length > 0) {
        this.renderFacetTerms('brands', brands.map((b) => ({
          id: b.id, label: b.name, count: b.productCount,
        })))
      }
      this.rerunSearch()
    })

    // Populate recent searches as removable chips below the search bar
    // (Doofinder-style). Hidden entirely when there are none.
    this.renderRecentChips()
    const recentClearBtn = this.shadowRoot.querySelector('.fs-recent-clear')
    recentClearBtn?.addEventListener('click', () => {
      this.clearRecentSearches()
      this.renderRecentChips()
    })

    // Event listeners
    closeBtn?.addEventListener('click', () => this.closeFullscreen())

    // Mobile filter drawer — the facets panel slides in from the left
    // when the "Filters" button in the results header is tapped. Backdrop
    // tap or the panel's own × button closes it. Desktop CSS hides the
    // open/close buttons + backdrop, making this a no-op above 768px.
    const facetsPanel = this.shadowRoot.querySelector('.fs-facets-panel') as HTMLElement | null
    const backdrop = this.shadowRoot.querySelector('.fs-sidebar-backdrop') as HTMLElement | null
    const openFiltersBtn = this.shadowRoot.querySelector('.fs-mobile-facets-btn') as HTMLElement | null
    const closeFiltersBtn = this.shadowRoot.querySelector('.fs-mobile-facets-close') as HTMLElement | null
    const openDrawer = () => {
      facetsPanel?.classList.add('is-open')
      backdrop?.classList.add('is-open')
    }
    const closeDrawer = () => {
      facetsPanel?.classList.remove('is-open')
      backdrop?.classList.remove('is-open')
    }
    openFiltersBtn?.addEventListener('click', openDrawer)
    closeFiltersBtn?.addEventListener('click', closeDrawer)
    backdrop?.addEventListener('click', closeDrawer)

    this.input?.addEventListener('input', (e) => {
      // Any keystroke in the search input means the user wants to start
      // a new search — collapse any open product/fitment detail views so
      // the results appear immediately without an extra back-click.
      this.popAllViews()
      const query = (e.target as HTMLInputElement).value.trim()
      this.handleInput(query)
    })
    this.input?.addEventListener('keydown', (e) => this.handleSearchKeydown(e))

    // Focusing the input also signals "new search" intent — clear any
    // detail view so it's obvious you're back in search mode.
    this.input?.addEventListener('focus', () => {
      if (this.viewStack.length > 1) this.popAllViews()
    })

    // Escape closes; Tab cycles focus within the overlay (focus trap).
    // The shadow DOM isolates our focusables from the host page's, so we
    // just need to detect Tab at the first/last focusable and wrap.
    const getFocusables = (): HTMLElement[] => {
      if (!this.shadowRoot) return []
      const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      return Array.from(this.shadowRoot.querySelectorAll<HTMLElement>(selector)).filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0
      )
    }

    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (facetsPanel?.classList.contains('is-open')) {
          closeDrawer()
          return
        }
        if (this.viewStack.length > 1) {
          this.popView()
        } else {
          this.closeFullscreen()
          document.removeEventListener('keydown', escHandler)
        }
        return
      }

      if (e.key === 'Tab') {
        const focusables = getFocusables()
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        // shadowRoot.activeElement is the currently-focused element inside shadow DOM
        const active = this.shadowRoot?.activeElement as HTMLElement | null

        if (e.shiftKey && active === first) {
          e.preventDefault()
          last?.focus()
        } else if (!e.shiftKey && active === last) {
          e.preventDefault()
          first?.focus()
        }
      }
    }
    this.activeKeyHandler = escHandler
    document.addEventListener('keydown', escHandler)

    // Click outside to close
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.closeFullscreen()
      }
    })

    // Focus input
    setTimeout(() => this.input?.focus(), 100)

    // Mobile back-button support — push a history entry so the OS back
    // button closes the overlay instead of leaving the site. Use a hash
    // marker so we can distinguish our entry from the page's own URL.
    try {
      history.pushState({ flareSearchOverlay: true }, '', `${location.pathname}${location.search}#fs-search`)
      this.didPushHistory = true
      const popHandler = () => {
        // User hit back: tear down the overlay. Don't call history.back()
        // again — popstate already advanced the history.
        this.didPushHistory = false // prevent closeFullscreen from re-popping
        this.closeFullscreen()
      }
      this.activePopstateHandler = popHandler
      window.addEventListener('popstate', popHandler)
    } catch {
      // Some browsers / sandboxes restrict pushState; degrade gracefully.
      this.didPushHistory = false
    }

    this.isOpen = true
  }

  private closeFullscreen(): void {
    if (!this.isOpen) return

    // Detach keyboard handler (Esc / Tab trap)
    if (this.activeKeyHandler) {
      document.removeEventListener('keydown', this.activeKeyHandler)
      this.activeKeyHandler = null
    }

    // Detach popstate handler + pop our pushed entry if the close was
    // programmatic (X button, click outside) rather than via back button.
    if (this.activePopstateHandler) {
      window.removeEventListener('popstate', this.activePopstateHandler)
      this.activePopstateHandler = null
    }
    if (this.didPushHistory) {
      this.didPushHistory = false
      try { history.back() } catch { /* ignore */ }
    }

    this.container?.remove()
    this.container = null
    this.shadowRoot = null
    this.input = null
    document.body.style.overflow = ''
    this.isOpen = false
    this.lastQuery = ''
    // Reset facet state so reopening is a clean slate
    this.filterCategory = null
    this.filterBrand = null
    this.filterType = null
    this.filterPriceMin = null
    this.filterPriceMax = null
    this.filterInStockOnly = false
    this.filterModelId = null
    this.sortOrder = 'relevance'
    this.liveFacetCounts = { brand: {}, category: {}, model: {} }
    this.viewStack = [{ type: 'search' }]
  }

  // ============================================
  // VIEW STACK + PRODUCT DETAIL
  // ============================================

  /**
   * Push a product detail view onto the navigation stack.
   * Fetches product details + similar products in parallel, renders
   * the detail view, and slides it in over the search results.
   */
  async pushProductView(productId: string): Promise<void> {
    const productView = this.shadowRoot?.querySelector('.fs-product-view') as HTMLElement | null
    if (!productView) return

    // Push state immediately so the back button works even before fetch completes
    this.viewStack.push({ type: 'product', productId })

    // Show a loading skeleton while fetching
    productView.innerHTML = `
			<div class="fs-product-loading">
				<div class="fs-spinner"></div>
				<span>Loading product...</span>
			</div>
		`
    productView.style.display = 'block'
    // Scroll the main container back to top so the detail view is
    // fully visible regardless of how far down the results they scrolled.
    const main = this.shadowRoot?.querySelector('.fs-main') as HTMLElement | null
    if (main) main.scrollTop = 0
    // Trigger the slide-in by adding the class on the next frame
    requestAnimationFrame(() => productView.classList.add('is-visible'))

    try {
      const [productResp, similarResp, fitmentsResp] = await Promise.all([
        fetch(`${this.config.apiBaseUrl}/api/search/product/${encodeURIComponent(productId)}`),
        fetch(
          `${this.config.apiBaseUrl}/api/search/similar/${encodeURIComponent(productId)}?limit=6`
        ).catch(() => null),
        fetch(
          `${this.config.apiBaseUrl}/api/search/product/${encodeURIComponent(productId)}/fitments`
        ).catch(() => null),
      ])

      if (!productResp.ok) {
        throw new Error(`Product fetch failed: ${productResp.status}`)
      }

      type KnownAlias = { partName: string; plateNumber: string | null; fitmentTitle: string; make: string }
      type ProductManual = { url: string; title: string | null; filename: string | null }
      const productPayload = (await productResp.json()) as {
        product: ProductDetails
        known_as?: KnownAlias[]
        manuals?: ProductManual[]
      }
      const product = productPayload.product
      let similar: SimilarProduct[] = []
      if (similarResp && similarResp.ok) {
        try {
          const similarData = (await similarResp.json()) as { products?: SimilarProduct[] }
          similar = similarData.products ?? []
        } catch {
          similar = []
        }
      }

      let fitsInto: Array<{ id: string; title: string; url: string | null; modelName: string | null }> = []
      if (fitmentsResp && fitmentsResp.ok) {
        try {
          const fd = (await fitmentsResp.json()) as {
            fitments?: Array<{ id: string; title: string; url: string | null; modelName: string | null }>
          }
          fitsInto = fd.fitments ?? []
        } catch {
          fitsInto = []
        }
      }

      const knownAs = productPayload.known_as ?? []
      const manuals = productPayload.manuals ?? []

      this.renderProductView(productView, product, similar, fitsInto, knownAs, manuals)
      this.trackEvent('quick_view', productId)
    } catch (err) {
      console.error('FlareSearch: product view error', err)
      productView.innerHTML = `
				<div class="fs-product-error">
					<button class="fs-product-back" type="button">← Back</button>
					<p>Couldn't load that product. <a href="#" class="fs-product-retry">Try again</a></p>
				</div>
			`
      productView.querySelector('.fs-product-back')?.addEventListener('click', () => this.popView())
      productView.querySelector('.fs-product-retry')?.addEventListener('click', (e) => {
        e.preventDefault()
        // Retry: pop and re-push
        this.popView()
        void this.pushProductView(productId)
      })
    }
  }

  /**
   * Push a fitment detail view onto the navigation stack.
   * Fetches the fitment (with all parts joined to products) and renders
   * a parts grid. Clicking a part pushes the product detail view on top.
   */
  async pushFitmentView(fitmentId: string): Promise<void> {
    const fitmentView = this.shadowRoot?.querySelector('.fs-fitment-view') as HTMLElement | null
    if (!fitmentView) return

    this.viewStack.push({ type: 'fitment', fitmentId })

    fitmentView.innerHTML = `
			<div class="fs-product-loading">
				<div class="fs-spinner"></div>
				<span>Loading fitment...</span>
			</div>
		`
    fitmentView.style.display = 'block'
    const fMain = this.shadowRoot?.querySelector('.fs-main') as HTMLElement | null
    if (fMain) fMain.scrollTop = 0
    requestAnimationFrame(() => fitmentView.classList.add('is-visible'))

    try {
      const resp = await fetch(
        `${this.config.apiBaseUrl}/api/search/fitment/${encodeURIComponent(fitmentId)}`
      )
      if (!resp.ok) throw new Error(`Fitment fetch failed: ${resp.status}`)
      const { fitment } = (await resp.json()) as {
        fitment: {
          id: string
          title: string
          url: string | null
          primaryCategory: string | null
          partsCount: number
          featuredImageUrl: string | null
          featuredImageThumb: string | null
          plateCode: string | null
          models: Array<{ name: string }>
          parts: Array<{
            sequence: number
            partName: string
            plateNumber: string | null
            oemNumber: string | null
            qty: string | null
            sku: string | null
            productId: string | null
            product: {
              id: string
              title: string | null
              price: number | null
              salePrice: number | null
              imageUrl: string | null
              inStock: boolean
            } | null
          }>
        }
      }
      this.renderFitmentView(fitmentView, fitment)
    } catch (err) {
      console.error('FlareSearch: fitment view error', err)
      fitmentView.innerHTML = `
				<div class="fs-product-error">
					<button class="fs-fitment-back" type="button">← Back</button>
					<p>Couldn't load that fitment.</p>
				</div>
			`
      fitmentView.querySelector('.fs-fitment-back')?.addEventListener('click', () => this.popView())
    }
  }

  private renderFitmentView(
    container: HTMLElement,
    fitment: {
      id: string
      title: string
      url: string | null
      primaryCategory: string | null
      partsCount: number
      featuredImageUrl: string | null
      featuredImageThumb: string | null
      plateCode: string | null
      models: Array<{ name: string }>
      parts: Array<{
        sequence: number
        partName: string
        plateNumber: string | null
        oemNumber: string | null
        qty: string | null
        sku: string | null
        productId: string | null
        product: {
          id: string
          title: string | null
          price: number | null
          salePrice: number | null
          imageUrl: string | null
          inStock: boolean
        } | null
      }>
    }
  ): void {
    const modelLabel = fitment.models.map((m) => this.escapeHtml(m.name)).join(', ')
    const cat = fitment.primaryCategory ? this.escapeHtml(fitment.primaryCategory) : ''

    // Parts render as a list (one row per part) rather than a grid.
    // A row layout reads better for auto parts catalogues where plate
    // number, name, OEM, and price all need to be scannable at a glance.
    const partRows = fitment.parts
      .map((part) => {
        const img = this.getProxiedImageUrl(part.product?.imageUrl ?? null)
        const imgHtml = img
          ? `<img src="${img}" alt="" loading="lazy">`
          : `<div class="fs-no-image"></div>`
        const price = part.product
          ? this.renderPriceHtml(
              {
                price: part.product.price,
                salePrice: part.product.salePrice,
              } as SearchResult,
              'fs-fp-price'
            )
          : '<span class="fs-part-unavailable">Not in catalogue</span>'
        const oosBadge =
          part.product && !part.product.inStock
            ? `<span class="fs-badge fs-badge-oos">Out of stock</span>`
            : ''
        const plate = part.plateNumber
          ? `<span class="fs-fp-plate">${this.escapeHtml(part.plateNumber)}</span>`
          : `<span class="fs-fp-plate fs-fp-plate-empty">${part.sequence + 1}</span>`
        const oem = part.oemNumber
          ? `<span class="fs-fp-oem">${this.escapeHtml(part.oemNumber)}</span>`
          : ''
        const qty =
          part.qty && part.qty !== '1'
            ? `<span class="fs-fp-qty">× ${this.escapeHtml(part.qty)}</span>`
            : ''
        const title = this.escapeHtml(part.product?.title ?? part.partName)
        const linked = part.product && part.productId
        const tag = linked ? 'button' : 'div'
        const attrs = linked
          ? `type="button" data-source-id="${this.escapeHtml(part.productId!)}"`
          : ''
        return `
			<${tag} class="fs-fp-row${linked ? ' is-linked' : ' is-unlinked'}" ${attrs}>
				${plate}
				<div class="fs-fp-image">${imgHtml}</div>
				<div class="fs-fp-info">
					<div class="fs-fp-title">${title}${qty}</div>
					${oem ? `<div class="fs-fp-meta">${oem}</div>` : ''}
				</div>
				<div class="fs-fp-right">
					${price}
					${oosBadge}
				</div>
			</${tag}>`
      })
      .join('')

    // Plate schematic image (featured image on the fitment post). Proxied
    // through /api/image so it benefits from the same edge cache as product
    // photos. Clickable to open the full-size original in a new tab.
    const plateHtml = fitment.featuredImageUrl
      ? `<div class="fs-fd-plate">
          <a href="${fitment.featuredImageUrl}" target="_blank" rel="noopener" aria-label="Open full-size diagram">
            <img src="${this.getProxiedImageUrl(fitment.featuredImageUrl)}" alt="${this.escapeHtml(fitment.title)} — parts diagram" loading="lazy">
          </a>
          ${fitment.plateCode ? `<div class="fs-fd-plate-label">Plate <strong>${this.escapeHtml(fitment.plateCode)}</strong></div>` : ''}
        </div>`
      : ''

    const html = `
			<div class="fs-fitment-detail">
				<div class="fs-fd-header">
					<div class="fs-fd-header-left">
						<button class="fs-fitment-back" type="button">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
							<span>Back to results</span>
						</button>
						<button class="fs-new-search" type="button" title="Start a new search">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
							<span>New search</span>
						</button>
					</div>
					${fitment.url ? `<a href="${fitment.url}" target="_blank" rel="noopener" class="fs-fd-link">View fitment page ↗</a>` : ''}
				</div>
				<div class="fs-fd-title-block">
					<h2 class="fs-fd-title">${this.escapeHtml(fitment.title)}</h2>
					<div class="fs-fitment-meta">${modelLabel}${cat ? ' · ' + cat : ''} · ${fitment.partsCount} part${fitment.partsCount === 1 ? '' : 's'}</div>
				</div>
				${plateHtml}
				<div class="fs-fp-list">${partRows}</div>
			</div>
		`
    // eslint-disable-next-line no-unsanitized/property -- all values escaped above
    container.innerHTML = html

    container.querySelector('.fs-fitment-back')?.addEventListener('click', () => this.popView())
    container.querySelector('.fs-new-search')?.addEventListener('click', () => {
      this.popAllViews()
      if (this.input) {
        this.input.value = ''
        this.input.focus()
      }
    })

    // Event delegation on the whole fitment-detail container. Catches
    // clicks on ANY descendant of a linked part row — the image, title,
    // price, plate badge — and walks up to find the row via closest().
    // This avoids the browser-specific issue where a click on a grid
    // child of a <button> doesn't reliably dispatch the button's own
    // click handler.
    container.addEventListener('click', (e) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      // Don't hijack clicks on the header buttons — they have their own handlers.
      if (target.closest('.fs-fitment-back, .fs-new-search, .fs-fd-link')) return
      const row = target.closest('.fs-fp-row.is-linked') as HTMLElement | null
      if (!row) return
      const sourceId = row.dataset['sourceId']
      if (!sourceId) return
      e.preventDefault()
      e.stopPropagation()
      void this.pushProductView(sourceId)
    })
  }

  /**
   * Pop the top of the view stack. If we're at the search base, this is
   * a no-op (closeFullscreen handles full exit).
   */
  popView(): void {
    if (this.viewStack.length <= 1) return
    const popped = this.viewStack.pop()

    const productView = this.shadowRoot?.querySelector('.fs-product-view') as HTMLElement | null
    const fitmentView = this.shadowRoot?.querySelector('.fs-fitment-view') as HTMLElement | null
    const view =
      popped?.type === 'fitment' ? fitmentView : popped?.type === 'product' ? productView : null
    if (!view) return

    // Slide out
    view.classList.remove('is-visible')
    // After the transition, hide the element so it doesn't intercept clicks
    setTimeout(() => {
      const top = this.viewStack[this.viewStack.length - 1]?.type
      // Only hide this view if nothing of the same type is on top
      if (top !== popped?.type) {
        view.style.display = 'none'
        view.replaceChildren()
      }
    }, 280)
  }

  /**
   * Collapse the whole view stack back to the base 'search' view.
   * Called when the user focuses or types in the search input — they
   * clearly want to start a new search, so any product/fitment detail
   * overlays should get out of the way.
   */
  popAllViews(): void {
    if (this.viewStack.length <= 1) return
    this.viewStack = [{ type: 'search' }]
    const productView = this.shadowRoot?.querySelector('.fs-product-view') as HTMLElement | null
    const fitmentView = this.shadowRoot?.querySelector('.fs-fitment-view') as HTMLElement | null
    for (const view of [productView, fitmentView]) {
      if (!view) continue
      view.classList.remove('is-visible')
      setTimeout(() => {
        view.style.display = 'none'
        view.replaceChildren()
      }, 280)
    }
  }

  /**
   * Render the product detail HTML into the given container and wire up
   * its interactive elements (back button, similar product clicks).
   */
  private renderProductView(
    container: HTMLElement,
    product: ProductDetails,
    similar: SimilarProduct[],
    fitsInto: Array<{ id: string; title: string; url: string | null; modelName: string | null }> = [],
    knownAs: Array<{ partName: string; plateNumber: string | null; fitmentTitle: string; make: string }> = [],
    manuals: Array<{ url: string; title: string | null; filename: string | null }> = []
  ): void {
    const imgSrc = this.getProxiedImageUrl(product.imageUrl)
    const outOfStock = !product.inStock

    // Brand + SKU subtitle
    const subtitleParts: string[] = []
    if (product.brand) subtitleParts.push(this.escapeHtml(product.brand))
    if (product.sku) subtitleParts.push(`SKU: ${this.escapeHtml(product.sku)}`)
    const subtitle = subtitleParts.join(' · ')

    // Categories as small tags
    const categoryNames: string[] = Array.isArray(product.categories)
      ? (product.categories as Array<string | { name: string }>)
          .map((c) => (typeof c === 'string' ? c : c.name))
          .filter(Boolean)
      : []

    // Price
    const hasSale =
      product.salePrice !== null &&
      product.salePrice !== undefined &&
      product.price !== null &&
      product.price !== undefined &&
      product.salePrice < product.price
    const priceHtml = hasSale
      ? `<div class="fs-pv-price-row">
           <span class="fs-pv-price-was">$${product.price?.toFixed(2)}</span>
           <span class="fs-pv-price-now">$${(product.salePrice as number).toFixed(2)}</span>
         </div>`
      : product.price !== null && product.price !== undefined
        ? `<div class="fs-pv-price-row">
             <span class="fs-pv-price">$${product.price.toFixed(2)}</span>
           </div>`
        : ''

    const stockHtml = outOfStock
      ? `<span class="fs-badge fs-badge-oos">Out of stock</span>`
      : `<span class="fs-badge fs-badge-instock">In stock</span>`

    // Description: WooCommerce returns HTML — normalise its paragraph
    // structure to plain text with \n\n separators, then escape, then
    // rebuild <p> tags so we never render raw HTML from the origin.
    const descHtml = product.description
      ? `<div class="fs-pv-description">${this.htmlToParagraphs(product.description)}</div>`
      : ''

    // Categories chip row
    const catsHtml =
      categoryNames.length > 0
        ? `<div class="fs-pv-cats">${categoryNames
            .map((c) => `<span class="fs-pv-cat">${this.escapeHtml(c)}</span>`)
            .join('')}</div>`
        : ''

    // Fits into (bike fitments that include this product).
    // Each row is an <a> to the WP fitment page (opens new tab) with an
    // explicit "View parts" button that pushes the in-widget fitment detail.
    const fitsIntoHtml =
      fitsInto.length > 0
        ? `<div class="fs-pv-fits">
            <h3 class="fs-pv-section-title">Fits these bikes</h3>
            <div class="fs-pv-fits-list">
              ${fitsInto
                .map((f) => {
                  const href = (f as { url?: string | null }).url || '#'
                  return `<a href="${this.escapeHtml(href)}" target="_blank" rel="noopener" class="fs-pv-fit-item" data-id="${this.escapeHtml(f.id)}">
                    <div class="fs-pv-fit-text">
                      <div class="fs-pv-fit-model">${this.escapeHtml(f.modelName || '')}</div>
                      <div class="fs-pv-fit-title">${this.escapeHtml(f.title)}</div>
                    </div>
                    <svg class="fs-pv-fit-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 17L17 7M7 7h10v10"/></svg>
                  </a>`
                })
                .join('')}
            </div>
          </div>`
        : ''

    // "Also known as" — distinct fitment-contextual names for the same
    // product (e.g. same SKU shown as "Oil pump relief" on one plate and
    // "Relief valve ball" on another). Dedupe by partName.
    const aliasMap = new Map<string, typeof knownAs>()
    for (const k of knownAs) {
      const key = k.partName.toLowerCase().trim()
      if (!aliasMap.has(key)) aliasMap.set(key, [])
      aliasMap.get(key)!.push(k)
    }
    // Only show if there's at least one alias that differs from the canonical title
    const productTitleLower = product.title.toLowerCase()
    const distinctAliases = [...aliasMap.entries()].filter(
      ([key]) => !productTitleLower.includes(key) && key.length >= 3
    )
    const aliasHtml =
      distinctAliases.length > 0
        ? `<div class="fs-pv-aliases">
            <h3 class="fs-pv-section-title">Also known as</h3>
            <ul class="fs-pv-alias-list">
              ${distinctAliases
                .slice(0, 8)
                .map(([, occurrences]) => {
                  const first = occurrences[0]!
                  const contexts = occurrences
                    .map((o) => {
                      const plate = o.plateNumber ? `plate ${this.escapeHtml(o.plateNumber)}` : ''
                      return `${this.escapeHtml(o.fitmentTitle)}${plate ? ` — ${plate}` : ''}`
                    })
                    .slice(0, 2)
                    .join(' · ')
                  return `<li>
                    <span class="fs-pv-alias-name">${this.escapeHtml(first.partName)}</span>
                    <span class="fs-pv-alias-context">${contexts}</span>
                  </li>`
                })
                .join('')}
            </ul>
          </div>`
        : ''

    // Manuals / PDFs matched to this product by SKU regex.
    const manualsHtml =
      manuals.length > 0
        ? `<div class="fs-pv-manuals">
            <h3 class="fs-pv-section-title">Manuals & diagrams</h3>
            <ul class="fs-pv-manual-list">
              ${manuals
                .map(
                  (m) => `<li>
                    <a href="${m.url}" target="_blank" rel="noopener">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      <span>${this.escapeHtml(m.title || m.filename || 'Document')}</span>
                    </a>
                  </li>`
                )
                .join('')}
            </ul>
          </div>`
        : ''

    // Similar products
    const similarHtml =
      similar.length > 0
        ? `<div class="fs-pv-similar">
            <h3 class="fs-pv-section-title">Similar products</h3>
            <div class="fs-pv-similar-grid">
              ${similar
                .map((s) => {
                  const sImg = this.getProxiedImageUrl(s.imageUrl)
                  const priceLabel =
                    s.salePrice && s.price && s.salePrice < s.price
                      ? `$${s.salePrice.toFixed(2)}`
                      : s.price !== null && s.price !== undefined
                        ? `$${s.price.toFixed(2)}`
                        : ''
                  return `<button type="button" class="fs-pv-similar-item" data-id="${this.escapeHtml(s.id)}">
                    <div class="fs-pv-similar-img">${
                      sImg
                        ? `<img src="${sImg}" alt="" loading="lazy">`
                        : `<div class="fs-no-image"></div>`
                    }</div>
                    <div class="fs-pv-similar-title">${this.escapeHtml(s.title)}</div>
                    ${priceLabel ? `<div class="fs-pv-similar-price">${priceLabel}</div>` : ''}
                  </button>`
                })
                .join('')}
            </div>
          </div>`
        : ''

    const html = `
			<div class="fs-pv-header">
				<button class="fs-product-back" type="button" aria-label="Back to results">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<path d="M19 12H5M12 19l-7-7 7-7"></path>
					</svg>
					<span>Back to results</span>
				</button>
				<button class="fs-new-search" type="button" title="Start a new search">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
					<span>New search</span>
				</button>
			</div>
			<div class="fs-pv-body${outOfStock ? ' fs-pv-oos' : ''}">
				<div class="fs-pv-image">
					${imgSrc ? `<img src="${imgSrc}" alt="${this.escapeHtml(product.title)}">` : `<div class="fs-no-image"></div>`}
				</div>
				<div class="fs-pv-info">
					<h2 class="fs-pv-title">${this.escapeHtml(product.title)}</h2>
					${subtitle ? `<div class="fs-pv-subtitle">${subtitle}</div>` : ''}
					<div class="fs-pv-meta-row">
						${priceHtml}
						${stockHtml}
					</div>
					${catsHtml}
					${descHtml}
				</div>
			</div>
			<div class="fs-pv-actions">
				${
				  product.addToCartUrl
				    ? `<a href="${product.addToCartUrl}" class="fs-pv-btn fs-pv-btn-primary" data-action="add-to-cart" data-product-id="${this.escapeHtml(product.id)}">Add to cart</a>`
				    : ''
				}
				${
				  product.url
				    ? `<a href="${product.url}" class="fs-pv-btn fs-pv-btn-secondary" target="_blank" rel="noopener">View on website</a>`
				    : ''
				}
			</div>
			${aliasHtml}
			${manualsHtml}
			${fitsIntoHtml}
			${similarHtml}
		`

    container.innerHTML = html

    // Wire up interactive elements
    container.querySelector('.fs-product-back')?.addEventListener('click', () => this.popView())
    container.querySelector('.fs-new-search')?.addEventListener('click', () => {
      this.popAllViews()
      if (this.input) {
        this.input.value = ''
        this.input.focus()
      }
    })

    container.querySelectorAll('.fs-pv-similar-item').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).dataset['id']
        if (id) {
          // Push another product view onto the stack — back button will return
          // to this product, then back again to search results
          void this.pushProductView(id)
        }
      })
    })

    container.querySelector('[data-action="add-to-cart"]')?.addEventListener('click', () => {
      const id = (container.querySelector('[data-action="add-to-cart"]') as HTMLElement)?.dataset[
        'productId'
      ]
      if (id) this.trackEvent('add_to_cart', id)
    })

    // Scroll back to top of detail view
    container.scrollTop = 0
  }

  private getFullscreenStyles(): string {
    const { primaryColor, backgroundColor, textColor } = this.config.theme

    return fullscreenStyles
      .replace(/var\(--fs-primary\)/g, primaryColor)
      .replace(/var\(--fs-bg\)/g, backgroundColor)
      .replace(/var\(--fs-text\)/g, textColor)
  }

  // ============================================
  // SHARED SEARCH LOGIC
  // ============================================
  private handleInput(query: string): void {
    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    // Update chip bars (recent vs suggestions) on every keystroke.
    // Fullscreen mode only — dropdown mode has its own empty-state handling.
    if (this.config.mode === 'fullscreen' || this.config.mode === 'trigger') {
      this.syncChipBars(query)
    }

    // Check minimum characters
    if (query.length < this.config.minChars) {
      if (this.config.mode === 'dropdown') {
        this.hideDropdown()
      } else {
        this.showInitialState()
      }
      return
    }

    // Show loading
    this.showLoading()
    if (this.config.mode === 'dropdown') {
      this.showDropdown()
    }

    // Debounce search
    this.debounceTimer = setTimeout(() => {
      this.search(query)
    }, this.config.debounceMs)
  }

  private async search(query: string): Promise<void> {
    this.lastQuery = query

    // Build request body including any active facet filters. Only include
    // keys the backend expects — undefined keys are skipped by JSON.stringify.
    const body: Record<string, unknown> = {
      query,
      // Fullscreen mode has a big grid and customers want to see lots of
      // products at once. Non-product types (fitment/brand/category/post/page)
      // can take ~14 slots via per-type caps, so we ask for 60 to leave plenty
      // of headroom for products. Server clamps to 60 (see routes.ts).
      limit: this.config.mode === 'fullscreen'
        ? 60
        : Math.max(this.config.maxResults * 2, 15),
    }
    if (this.filterType) body['types'] = [this.filterType]
    if (this.filterBrand) body['brand'] = this.filterBrand
    if (this.filterCategory) body['category'] = this.filterCategory
    if (this.filterModelId) body['modelId'] = this.filterModelId
    if (this.filterPriceMin !== null) body['priceMin'] = this.filterPriceMin
    if (this.filterPriceMax !== null) body['priceMax'] = this.filterPriceMax
    if (this.filterInStockOnly) body['inStock'] = 1
    if (this.sortOrder !== 'relevance') body['sort'] = this.sortOrder

    try {
      const response = await fetch(`${this.config.apiBaseUrl}/api/search/autocomplete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`)
      }

      const data: AutocompleteResponse = await response.json()

      // Capture live facet counts so the chip rendering can show them
      if (data.facetCounts) {
        this.liveFacetCounts = {
          brand: data.facetCounts.brand ?? {},
          category: data.facetCounts.category ?? {},
          model: data.facetCounts.model ?? {},
        }
        // Re-render chips with the new live counts (fullscreen mode only —
        // dropdown mode doesn't show the facet sidebar)
        if (this.config.mode === 'fullscreen') {
          this.refreshFilterChips()
        }
      }

      // Recent searches are saved on Enter or a result click, never here:
      // this runs on every debounced keystroke (issue #14).

      if (this.config.mode === 'dropdown') {
        this.renderDropdownResults(data)
      } else {
        this.renderFullscreenResults(data)
      }
    } catch (error) {
      console.error('FlareSearch: Search error', error)
      this.showEmpty()
    }
  }

  // Order in which type-sections are rendered. Products first (that's
  // what most search intents want), then fitments/categories/brands as
  // secondary discovery aids, then editorial content last.
  private static readonly TYPE_ORDER: ResultType[] = ['product', 'fitment', 'category', 'brand', 'post', 'page']

  private static readonly TYPE_LABELS: Record<ResultType, string> = {
    fitment: 'Bike fitments',
    product: 'Products',
    post: 'Articles',
    page: 'Pages',
    category: 'Categories',
    brand: 'Brands',
  }

  /**
   * Group results by type, preserving the API's relevance order within each group.
   */
  private groupByType(results: SearchResult[]): Map<ResultType, SearchResult[]> {
    const grouped = new Map<ResultType, SearchResult[]>()
    for (const r of results) {
      const t = r.type
      if (!grouped.has(t)) grouped.set(t, [])
      grouped.get(t)!.push(r)
    }
    return grouped
  }

  /**
   * Render a single result card. Picks a sub-template based on type:
   *  - product: image + title + brand/sku + price + stock badge (rich)
   *  - post/page: icon + title + excerpt
   *  - category/brand: icon + name + product count
   */
  private renderResultCard(result: SearchResult, mode: 'dropdown' | 'fullscreen'): string {
    const prefix = mode === 'dropdown' ? 'flaresearch-result' : 'fs-result-card'
    const imagePrefix = mode === 'dropdown' ? 'flaresearch-result-image' : 'fs-result-image'
    const infoPrefix = mode === 'dropdown' ? 'flaresearch-result-info' : 'fs-result-info'
    const titlePrefix = mode === 'dropdown' ? 'flaresearch-result-title' : 'fs-result-title'
    const skuPrefix = mode === 'dropdown' ? 'flaresearch-result-sku' : 'fs-result-sku'
    const metaPrefix = mode === 'dropdown' ? 'flaresearch-result-meta' : 'fs-result-meta'
    const badgePrefix = mode === 'dropdown' ? 'flaresearch-badge' : 'fs-badge'
    const pricePrefix = mode === 'dropdown' ? 'flaresearch-result-price' : 'fs-result-price'

    if (result.type === 'product') {
      const imgSrc = this.getProxiedImageUrl(result.imageUrl)
      const outOfStock = result.inStock === false
      const brandHtml = result.brand
        ? `<span class="${prefix}-brand">${this.escapeHtml(result.brand)}</span>`
        : ''
      const skuHtml = result.sku
        ? `<div class="${skuPrefix}">${brandHtml}${brandHtml ? ' · ' : ''}${this.escapeHtml(result.sku)}</div>`
        : brandHtml
          ? `<div class="${skuPrefix}">${brandHtml}</div>`
          : ''
      const badgeHtml = outOfStock
        ? `<span class="${badgePrefix} ${badgePrefix}-oos">Out of stock</span>`
        : ''
      // Doofinder-style "-XX% OFF" badge in the top-right of the image,
      // shown whenever the product has a real sale (salePrice < price).
      const hasSale =
        result.salePrice !== null &&
        result.salePrice !== undefined &&
        result.price !== null &&
        result.price !== undefined &&
        result.salePrice < result.price
      const percentOff =
        hasSale && result.price && result.salePrice
          ? Math.round(((result.price - result.salePrice) / result.price) * 100)
          : 0
      const saleBadgeHtml =
        hasSale && percentOff > 0 && !outOfStock
          ? `<span class="${badgePrefix} ${badgePrefix}-sale">-${percentOff}% OFF</span>`
          : ''
      const priceHtml = this.renderPriceHtml(result, pricePrefix)
      return `
			<a href="${result.url || '#'}" class="${prefix}${outOfStock ? ` ${prefix}-oos` : ''}" data-id="${result.id}" data-type="product" data-source-id="${this.escapeHtml(result.sourceId)}">
				<div class="${imagePrefix}">
					${imgSrc ? `<img src="${imgSrc}" alt="" loading="lazy">` : `<div class="${imagePrefix === 'fs-result-image' ? 'fs-no-image' : 'flaresearch-no-image'}"></div>`}
					${mode === 'fullscreen' && outOfStock ? badgeHtml : ''}
					${saleBadgeHtml}
				</div>
				<div class="${infoPrefix}">
					<div class="${titlePrefix}">${this.highlightQuery(result.title, this.lastQuery)}</div>
					${skuHtml}
					<div class="${metaPrefix}">${priceHtml}${mode === 'dropdown' ? badgeHtml : ''}</div>
				</div>
			</a>`
    }

    if (result.type === 'post' || result.type === 'page') {
      const icon = result.type === 'post'
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>'
        : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line></svg>'
      const excerpt = result.body
        ? `<div class="${prefix}-excerpt">${this.escapeHtml(this.truncateText(result.body, 120))}</div>`
        : ''
      return `
			<a href="${result.url || '#'}" class="${prefix} ${prefix}-article" data-id="${result.id}" data-type="${result.type}">
				<div class="${prefix}-icon">${icon}</div>
				<div class="${infoPrefix}">
					<div class="${titlePrefix}">${this.highlightQuery(result.title, this.lastQuery)}</div>
					${excerpt}
				</div>
			</a>`
    }

    if (result.type === 'category') {
      const icon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>'
      const count = (result.meta?.['productCount'] as number) ?? 0
      return `
			<button type="button" class="${prefix} ${prefix}-category" data-id="${result.id}" data-type="category" data-name="${this.escapeHtml(result.title)}">
				<div class="${prefix}-icon">${icon}</div>
				<div class="${infoPrefix}">
					<div class="${titlePrefix}">${this.highlightQuery(result.title, this.lastQuery)}</div>
					<div class="${prefix}-excerpt">Filter to ${count} products</div>
				</div>
			</button>`
    }

    if (result.type === 'fitment') {
      const icon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/><path d="M15 6l-3 4-5 7.5"/><path d="M15 6h4l1 4"/></svg>'
      const meta = result.meta as Record<string, unknown> | null
      const partsCount = (meta?.['partsCount'] as number) ?? 0
      const modelsArr = (meta?.['models'] as Array<{ name: string }>) ?? []
      const modelLabel = modelsArr[0]?.name || result.subtitle || ''
      return `
			<button type="button" class="${prefix} ${prefix}-fitment" data-id="${result.id}" data-type="fitment" data-source-id="${this.escapeHtml(result.sourceId)}">
				<div class="${prefix}-icon">${icon}</div>
				<div class="${infoPrefix}">
					<div class="${titlePrefix}">${this.highlightQuery(result.title, this.lastQuery)}</div>
					<div class="${prefix}-excerpt">${this.escapeHtml(modelLabel)} · ${partsCount} part${partsCount === 1 ? '' : 's'}</div>
				</div>
			</button>`
    }

    if (result.type === 'brand') {
      const icon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>'
      const count = (result.meta?.['productCount'] as number) ?? 0
      return `
			<button type="button" class="${prefix} ${prefix}-brand-row" data-id="${result.id}" data-type="brand" data-name="${this.escapeHtml(result.title)}">
				<div class="${prefix}-icon">${icon}</div>
				<div class="${infoPrefix}">
					<div class="${titlePrefix}">${this.highlightQuery(result.title, this.lastQuery)}</div>
					<div class="${prefix}-excerpt">Filter to ${count} products</div>
				</div>
			</button>`
    }

    return ''
  }

  /**
   * Render the type-grouped results into a container element.
   * dropdown vs fullscreen differ only in CSS class prefixes.
   */
  private renderGroupedSections(
    container: Element,
    results: SearchResult[],
    mode: 'dropdown' | 'fullscreen'
  ): void {
    const grouped = this.groupByType(results)
    const sectionPrefix = mode === 'dropdown' ? 'flaresearch-section' : 'fs-section'

    const sections: string[] = []
    for (const type of FlareSearchWidget.TYPE_ORDER) {
      const items = grouped.get(type)
      if (!items || items.length === 0) continue

      // For products in dropdown mode, show all of them; otherwise cap to a few
      // so non-product sections don't dominate
      const limit = type === 'product' ? items.length : Math.min(items.length, 3)
      const slice = items.slice(0, limit)

      const cards = slice.map((r) => this.renderResultCard(r, mode)).join('')
      const label = FlareSearchWidget.TYPE_LABELS[type]
      sections.push(`
			<div class="${sectionPrefix}">
				<div class="${sectionPrefix}-header">${label} <span class="${sectionPrefix}-count">${items.length}</span></div>
				<div class="${sectionPrefix}-items">${cards}</div>
			</div>`)
    }

    const tmp = document.createElement('div')
    // eslint-disable-next-line no-unsanitized/property -- all values escaped above
    tmp.innerHTML = sections.join('')
    container.replaceChildren(...Array.from(tmp.childNodes))

    this.attachResultClickHandlers(container)
  }

  private renderDropdownResults(data: AutocompleteResponse, _limit?: number): void {
    const resultsEl = this.shadowRoot?.querySelector('.flaresearch-results')
    if (!resultsEl) return

    // In dropdown mode, fitments clutter the compact results list.
    // Filter them out and show a subtle summary line at the bottom
    // instead — "4 fitment guides match 'X' — view all →".
    const fitments = data.results?.filter((r) => r.type === 'fitment') ?? []
    const nonFitments = data.results?.filter((r) => r.type !== 'fitment') ?? []

    if (nonFitments.length === 0 && fitments.length === 0) {
      this.showEmpty()
      return
    }

    this.hideLoading()
    this.hideEmpty()

    // Render non-fitment results normally
    if (nonFitments.length > 0) {
      this.renderGroupedSections(resultsEl, nonFitments, 'dropdown')
    } else {
      resultsEl.replaceChildren()
    }

    // Append a fitment summary line if any matched
    if (fitments.length > 0) {
      const query = this.escapeHtml(this.lastQuery || '')
      const line = document.createElement('div')
      line.className = 'flaresearch-fitment-summary'
      line.innerHTML = `<span>📖 ${fitments.length} fitment guide${fitments.length === 1 ? '' : 's'} match${fitments.length === 1 ? 'es' : ''} "${query}"</span><span class="flaresearch-fitment-link">View all →</span>`
      line.addEventListener('click', () => {
        // Close dropdown, reopen in fullscreen mode with the same query
        // so the user can browse fitments with full filtering.
        this.hideDropdown()
        this.openFullscreen()
        setTimeout(() => {
          if (this.input) {
            this.input.value = this.lastQuery
            this.input.dispatchEvent(new Event('input', { bubbles: true }))
          }
        }, 200)
      })
      resultsEl.appendChild(line)
    }
  }

  private renderFullscreenResults(data: AutocompleteResponse, _limit?: number): void {
    const resultsGrid = this.shadowRoot?.querySelector('.fs-results-grid')
    const countEl = this.shadowRoot?.querySelector('.fs-results-count strong')

    if (!resultsGrid) return

    if (!data.results || data.results.length === 0) {
      this.showEmpty()
      return
    }

    this.hideLoading()
    this.hideEmpty()
    this.setBodyState('results')

    // Doofinder-style: products only in the grid. Other types (fitments,
    // categories, brands, posts, pages) appear in their respective UI
    // surfaces (facets, dedicated tabs) but not in the main result grid.
    const productResults = data.results.filter((r) => r.type === 'product')
    if (countEl) countEl.textContent = String(productResults.length)

    if (productResults.length === 0) {
      this.showEmpty()
      return
    }

    // Render flat product grid (no per-type section wrappers anymore).
    const cards = productResults
      .map((r) => this.renderResultCard(r, 'fullscreen'))
      .join('')
    ;(resultsGrid as HTMLElement).innerHTML = cards
    this.attachResultClickHandlers(resultsGrid)
  }

  private attachResultClickHandlers(container: Element): void {
    container.querySelectorAll('[data-id]').forEach((el, index) => {
      const element = el as HTMLElement
      const docId = element.dataset['id']!
      const type = (element.dataset['type'] || 'product') as ResultType

      // The legacy Quick View eye-icon (still present in dropdown mode).
      // In fullscreen mode the entire card is the affordance — clicking
      // anywhere pushes the in-search product detail view.
      const qvBtn = el.querySelector('.flaresearch-quick-view, .fs-quick-view')
      if (qvBtn && type === 'product') {
        qvBtn.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          const sourceId = element.dataset['sourceId'] || docId
          if (this.config.mode === 'fullscreen') {
            void this.pushProductView(sourceId)
          } else {
            this.openQuickView(sourceId)
          }
        })
      }

      // Main click behaviour depends on type
      el.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.flaresearch-quick-view, .fs-quick-view')) {
          return
        }

        // Choosing a result commits the query that produced it (issue #14).
        this.saveRecentSearch(this.lastQuery)

        if (type === 'fitment') {
          e.preventDefault()
          if (this.config.mode === 'fullscreen') {
            void this.pushFitmentView(docId)
          }
          return
        }

        if (type === 'category' || type === 'brand') {
          // Don't navigate — toggle the filter and re-run search
          e.preventDefault()
          const name = element.dataset['name'] || ''
          if (!name) return
          if (type === 'category') {
            this.filterCategory = this.filterCategory === name ? null : name
          } else {
            this.filterBrand = this.filterBrand === name ? null : name
          }
          this.refreshFilterChips()
          this.rerunSearch()
          return
        }

        // Doofinder behaviour: clicking a product card navigates straight
        // to the product page on the host site (Ryan's specific request).
        // We track the click for analytics, then let the anchor's default
        // navigation happen. No in-overlay detail view.
        const sourceId = element.dataset['sourceId'] || docId
        this.trackClick(sourceId, index)
        if (this.config.mode === 'fullscreen') {
          this.closeFullscreen()
        }
      })
    })
  }

  /**
   * Re-render the facet checkbox lists after a filter changes (e.g. via a
   * category/brand click in the result list, or live counts updating from
   * the latest search response).
   */
  private refreshFilterChips(): void {
    const cats = this.storeInfo?.categories ?? []
    const brands = this.storeInfo?.brands ?? []
    if (cats.length > 0) {
      this.renderFacetTerms('categories', cats.map((c) => ({
        id: c.id, label: c.name, count: c.productCount,
      })))
    }
    if (brands.length > 0) {
      this.renderFacetTerms('brands', brands.map((b) => ({
        id: b.id, label: b.name, count: b.productCount,
      })))
    }
  }

  private highlightQuery(text: string, query: string): string {
    if (!query) return text
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    return text.replace(regex, '<mark>$1</mark>')
  }

  /**
   * Re-run the current search with the latest filter state.
   * If the user hasn't typed anything yet, skip — there's nothing to filter.
   */
  private rerunSearch(): void {
    const query = this.input?.value.trim() || this.lastQuery
    if (query && query.length >= this.config.minChars) {
      this.showLoading()
      void this.search(query)
    }
  }

  /**
   * Escape HTML entities for safe interpolation into template literals.
   * Used for fields we echo directly from the API (SKU, titles) where
   * the store owner might have entered characters like &, <, > in metadata.
   */
  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  /**
   * Convert WordPress/WooCommerce HTML descriptions into safe <p> markup.
   *
   * The origin returns HTML like "<p>Clutch pressure plate.</p><p>For:<br>
   * Triumph T100...</p>" and we want to preserve those paragraph breaks
   * without ever rendering raw HTML from the origin.
   *
   *   1. Normalise block boundaries to \n\n markers
   *   2. Turn <br> into \n
   *   3. Strip all remaining tags
   *   4. Decode HTML entities the origin may have left in there
   *   5. Escape for safe injection
   *   6. Split on \n\n and rebuild as <p>…<br>…</p>
   */
  private htmlToParagraphs(html: string): string {
    const plain = html
      .replace(/<\/(p|div|li|h[1-6])>/gi, '\n\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
      .replace(/\n{3,}/g, '\n\n')
      .trim()

    if (!plain) return ''

    return plain
      .split(/\n\n+/)
      .map((para) => {
        const lines = para.split('\n').map((l) => this.escapeHtml(l.trim())).filter(Boolean)
        return `<p>${lines.join('<br>')}</p>`
      })
      .join('')
  }

  /**
   * Render price HTML. Handles sale-price strikethrough when both price and
   * salePrice are present and salePrice < price. If taxRatePercent is set,
   * prices are multiplied by (1 + rate/100) and the taxLabel is appended.
   */
  private renderPriceHtml(result: SearchResult, baseClass: string): string {
    if (result.price === null || result.price === undefined) return ''
    const taxMult = 1 + (this.config.taxRatePercent || 0) / 100
    const taxLabelHtml =
      this.config.taxRatePercent > 0
        ? ` <span class="${baseClass}-tax">${this.escapeHtml(this.config.taxLabel)}</span>`
        : ''
    const displayPrice = (result.price * taxMult).toFixed(2)
    const hasSale =
      result.salePrice !== null &&
      result.salePrice !== undefined &&
      result.salePrice < result.price
    if (hasSale) {
      const displaySale = ((result.salePrice as number) * taxMult).toFixed(2)
      return `<div class="${baseClass} ${baseClass}-sale">
        <span class="${baseClass}-was">$${displayPrice}</span>
        <span class="${baseClass}-now">$${displaySale}${taxLabelHtml}</span>
      </div>`
    }
    return `<div class="${baseClass}">$${displayPrice}${taxLabelHtml}</div>`
  }

  /**
   * Get proxied image URL for edge caching
   * Base64 encodes the URL and routes through /api/image proxy
   */
  private getProxiedImageUrl(originalUrl: string | null): string | null {
    if (!originalUrl) return null
    // Base64 encode the URL for safe transport
    const encoded = btoa(originalUrl)
    return `${this.config.apiBaseUrl}/api/image?url=${encoded}`
  }

  /**
   * Track a user event
   */
  private async trackEvent(
    eventType: 'view' | 'quick_view' | 'search' | 'click' | 'add_to_cart' | 'filter',
    productId?: string,
    resultPosition?: number,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    try {
      await fetch(`${this.config.apiBaseUrl}/api/search/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.sessionId,
          eventType,
          productId: productId || undefined,
          query: this.lastQuery || undefined,
          resultPosition: resultPosition ?? undefined,
          metadata: metadata ?? undefined,
        }),
        keepalive: true,
      })
    } catch {
      // Silent fail - tracking should never block UX
    }
  }

  private async trackClick(productId: string, position?: number): Promise<void> {
    await this.trackEvent('click', productId, position)
  }

  /**
   * Open quick view modal for a product
   */
  async openQuickView(productId: string): Promise<void> {
    // Track quick view event
    this.trackEvent('quick_view', productId)

    try {
      // Fetch product details and similar products in parallel
      const [productRes, similarRes] = await Promise.all([
        fetch(`${this.config.apiBaseUrl}/api/search/product/${productId}`),
        fetch(`${this.config.apiBaseUrl}/api/search/similar/${productId}?limit=4`),
      ])

      if (!productRes.ok) {
        throw new Error('Failed to fetch product details')
      }

      const { product } = (await productRes.json()) as { product: ProductDetails }
      const { similar } = similarRes.ok
        ? ((await similarRes.json()) as { similar: SimilarProduct[] })
        : { similar: [] }

      this.showQuickViewModal(product, similar)
    } catch (error) {
      console.error('FlareSearch: Quick view error', error)
    }
  }

  /**
   * Show quick view modal with product details
   */
  private showQuickViewModal(product: ProductDetails, similar: SimilarProduct[]): void {
    // Remove existing modal if any
    this.closeQuickView()

    // Create modal container
    this.quickViewContainer = document.createElement('div')
    this.quickViewContainer.id = 'flaresearch-quickview'

    // Create shadow root for style isolation
    const shadow = this.quickViewContainer.attachShadow({ mode: 'open' })

    // Inject styles
    const styleEl = document.createElement('style')
    styleEl.textContent = this.getQuickViewStyles()
    shadow.appendChild(styleEl)

    // Get proxied image URL
    const imgSrc = this.getProxiedImageUrl(product.imageUrl)

    // Build modal HTML
    const modal = document.createElement('div')
    modal.className = 'qv-overlay'
    modal.innerHTML = `
      <div class="qv-modal">
        <button class="qv-close" aria-label="Close">&times;</button>
        <div class="qv-content">
          <div class="qv-image">
            ${imgSrc ? `<img src="${imgSrc}" alt="${product.title}" />` : '<div class="qv-no-image">No image</div>'}
          </div>
          <div class="qv-details">
            <h2 class="qv-title">${product.title}</h2>
            ${product.category ? `<p class="qv-category">${product.category}</p>` : ''}
            <div class="qv-price-row">
              ${
                product.salePrice && product.salePrice < (product.price || 0)
                  ? `<span class="qv-price qv-sale">$${product.salePrice.toFixed(2)}</span>
                     <span class="qv-price qv-original">$${product.price?.toFixed(2)}</span>`
                  : product.price
                    ? `<span class="qv-price">$${product.price.toFixed(2)}</span>`
                    : ''
              }
              ${product.inStock ? '<span class="qv-stock qv-in-stock">In Stock</span>' : '<span class="qv-stock qv-out-stock">Out of Stock</span>'}
            </div>
            ${product.description ? `<p class="qv-description">${this.truncateText(product.description, 300)}</p>` : ''}
            <div class="qv-actions">
              ${product.addToCartUrl ? `<a href="${product.addToCartUrl}" class="qv-btn qv-btn-primary" data-action="add-to-cart" data-product-id="${product.id}">Add to Cart</a>` : ''}
              ${product.url ? `<a href="${product.url}" class="qv-btn qv-btn-secondary">View Full Details</a>` : ''}
            </div>
          </div>
        </div>
        ${
          similar.length > 0
            ? `
          <div class="qv-similar">
            <h3>Similar Products</h3>
            <div class="qv-similar-grid">
              ${similar
                .map((s) => {
                  const simgSrc = this.getProxiedImageUrl(s.imageUrl)
                  return `
                  <a href="${s.url || '#'}" class="qv-similar-item" data-id="${s.id}">
                    <div class="qv-similar-image">
                      ${simgSrc ? `<img src="${simgSrc}" alt="${s.title}" loading="lazy" />` : '<div class="qv-no-image"></div>'}
                    </div>
                    <div class="qv-similar-title">${this.truncateText(s.title, 50)}</div>
                    ${s.price ? `<div class="qv-similar-price">$${s.price.toFixed(2)}</div>` : ''}
                  </a>
                `
                })
                .join('')}
            </div>
          </div>
        `
            : ''
        }
      </div>
    `
    shadow.appendChild(modal)

    // Add to document
    document.body.appendChild(this.quickViewContainer)
    document.body.style.overflow = 'hidden'

    // Event listeners
    const closeBtn = shadow.querySelector('.qv-close')
    closeBtn?.addEventListener('click', () => this.closeQuickView())

    // Click overlay to close
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeQuickView()
      }
    })

    // Escape key to close
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.closeQuickView()
        document.removeEventListener('keydown', escHandler)
      }
    }
    document.addEventListener('keydown', escHandler)

    // Track add to cart clicks
    const addToCartBtn = shadow.querySelector('[data-action="add-to-cart"]')
    addToCartBtn?.addEventListener('click', () => {
      const pid = (addToCartBtn as HTMLElement).dataset['productId']
      if (pid) {
        this.trackEvent('add_to_cart', pid)
      }
    })

    // Similar product clicks
    shadow.querySelectorAll('.qv-similar-item').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault()
        const pid = (el as HTMLElement).dataset['id']
        if (pid) {
          this.closeQuickView()
          this.openQuickView(pid)
        }
      })
    })
  }

  /**
   * Close quick view modal
   */
  closeQuickView(): void {
    if (this.quickViewContainer) {
      this.quickViewContainer.remove()
      this.quickViewContainer = null
      document.body.style.overflow = ''
    }
  }

  /**
   * Truncate text to a maximum length
   */
  private truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength).trim() + '...'
  }

  /**
   * Get quick view modal styles
   */
  private getQuickViewStyles(): string {
    const { primaryColor, backgroundColor, textColor } = this.config.theme

    return `
      .qv-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.6);
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        animation: qvFadeIn 0.2s ease;
      }

      @keyframes qvFadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .qv-modal {
        background: ${backgroundColor};
        color: ${textColor};
        border-radius: 12px;
        max-width: 900px;
        max-height: 90vh;
        width: 100%;
        overflow-y: auto;
        position: relative;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        animation: qvSlideIn 0.2s ease;
      }

      @keyframes qvSlideIn {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }

      .qv-close {
        position: absolute;
        top: 12px;
        right: 12px;
        background: none;
        border: none;
        font-size: 28px;
        cursor: pointer;
        color: ${textColor};
        opacity: 0.6;
        z-index: 10;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: all 0.2s;
      }

      .qv-close:hover {
        opacity: 1;
        background: rgba(0, 0, 0, 0.1);
      }

      .qv-content {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
        padding: 24px;
      }

      @media (max-width: 640px) {
        .qv-content {
          grid-template-columns: 1fr;
        }
      }

      .qv-image img {
        width: 100%;
        height: auto;
        border-radius: 8px;
        object-fit: contain;
      }

      .qv-no-image {
        width: 100%;
        aspect-ratio: 1;
        background: #f3f4f6;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #9ca3af;
      }

      .qv-details {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .qv-title {
        font-size: 24px;
        font-weight: 600;
        margin: 0;
        line-height: 1.3;
      }

      .qv-category {
        color: ${primaryColor};
        font-size: 14px;
        margin: 0;
      }

      .qv-price-row {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
      }

      .qv-price {
        font-size: 24px;
        font-weight: 700;
      }

      .qv-price.qv-sale {
        color: #dc2626;
      }

      .qv-price.qv-original {
        font-size: 18px;
        text-decoration: line-through;
        opacity: 0.6;
      }

      .qv-stock {
        font-size: 12px;
        padding: 4px 8px;
        border-radius: 4px;
        font-weight: 500;
      }

      .qv-in-stock {
        background: #dcfce7;
        color: #166534;
      }

      .qv-out-stock {
        background: #fee2e2;
        color: #991b1b;
      }

      .qv-description {
        font-size: 14px;
        line-height: 1.6;
        opacity: 0.8;
        margin: 0;
      }

      .qv-actions {
        display: flex;
        gap: 12px;
        margin-top: 12px;
      }

      .qv-btn {
        padding: 12px 24px;
        border-radius: 8px;
        font-weight: 600;
        text-decoration: none;
        text-align: center;
        cursor: pointer;
        transition: all 0.2s;
        flex: 1;
      }

      .qv-btn-primary {
        background: ${primaryColor};
        color: white;
      }

      .qv-btn-primary:hover {
        filter: brightness(1.1);
      }

      .qv-btn-secondary {
        background: transparent;
        border: 2px solid ${primaryColor};
        color: ${primaryColor};
      }

      .qv-btn-secondary:hover {
        background: ${primaryColor}10;
      }

      .qv-similar {
        padding: 24px;
        border-top: 1px solid rgba(0, 0, 0, 0.1);
      }

      .qv-similar h3 {
        font-size: 16px;
        font-weight: 600;
        margin: 0 0 16px 0;
      }

      .qv-similar-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 16px;
      }

      @media (max-width: 640px) {
        .qv-similar-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      .qv-similar-item {
        text-decoration: none;
        color: ${textColor};
        transition: transform 0.2s;
      }

      .qv-similar-item:hover {
        transform: translateY(-2px);
      }

      .qv-similar-image img {
        width: 100%;
        aspect-ratio: 1;
        object-fit: contain;
        padding: 4px;
        box-sizing: border-box;
        background: #f3f4f6;
        border-radius: 8px;
      }

      .qv-similar-image .qv-no-image {
        aspect-ratio: 1;
        font-size: 12px;
      }

      .qv-similar-title {
        font-size: 13px;
        margin-top: 8px;
        line-height: 1.3;
      }

      .qv-similar-price {
        font-size: 14px;
        font-weight: 600;
        margin-top: 4px;
      }
    `
  }

  // ============================================
  // UI STATE HELPERS
  // ============================================
  private showDropdown(): void {
    const dropdown = this.shadowRoot?.querySelector('.flaresearch-dropdown') as HTMLElement
    if (dropdown) {
      dropdown.style.display = 'block'
      this.isOpen = true
    }
  }

  private hideDropdown(): void {
    const dropdown = this.shadowRoot?.querySelector('.flaresearch-dropdown') as HTMLElement
    if (dropdown) {
      dropdown.style.display = 'none'
      this.isOpen = false
    }
  }

  private showLoading(): void {
    if (this.config.mode === 'dropdown') {
      const loadingEl = this.shadowRoot?.querySelector('.flaresearch-loading') as HTMLElement
      const resultsEl = this.shadowRoot?.querySelector('.flaresearch-results') as HTMLElement
      const emptyEl = this.shadowRoot?.querySelector('.flaresearch-empty') as HTMLElement
      if (loadingEl) loadingEl.style.display = 'flex'
      if (resultsEl) resultsEl.innerHTML = ''
      if (emptyEl) emptyEl.style.display = 'none'
    } else {
      this.setBodyState('loading')
    }
  }

  private hideLoading(): void {
    const loadingEl = this.shadowRoot?.querySelector(
      this.config.mode === 'dropdown' ? '.flaresearch-loading' : '.fs-loading'
    ) as HTMLElement
    if (loadingEl) loadingEl.style.display = 'none'
  }

  private showEmpty(): void {
    this.hideLoading()

    // Build a human-readable list of currently active filters.
    // Includes brand, category, model, type, stock, and price — every
    // facet that can produce a no-results state. The empty-state Clear
    // button below uses the same set so it actually clears them all.
    const activeFilters: string[] = []
    if (this.filterBrand) activeFilters.push(`brand <strong>${this.escapeHtml(this.filterBrand)}</strong>`)
    if (this.filterCategory) activeFilters.push(`category <strong>${this.escapeHtml(this.filterCategory)}</strong>`)
    if (this.filterModelId) {
      const modelName = this.storeInfo?.models?.find((m) => m.id === this.filterModelId)?.name
      activeFilters.push(`bike <strong>${this.escapeHtml(modelName || this.filterModelId)}</strong>`)
    }
    if (this.filterType) activeFilters.push(`type <strong>${this.filterType}</strong>`)
    if (this.filterInStockOnly) activeFilters.push(`<strong>in stock only</strong>`)
    if (this.filterPriceMin !== null || this.filterPriceMax !== null) {
      const min = this.filterPriceMin !== null ? `$${this.filterPriceMin}` : ''
      const max = this.filterPriceMax !== null ? `$${this.filterPriceMax}` : ''
      activeFilters.push(`price <strong>${min}${min && max ? '–' : ''}${max}</strong>`)
    }
    const hasFilters = activeFilters.length > 0

    // Build suggestion chips from store info (popular searches + top categories)
    const popular = this.storeInfo?.popularSearches ?? this.config.popularSearches ?? []
    const topCats = (this.storeInfo?.categories ?? [])
      .slice()
      .sort((a, b) => b.productCount - a.productCount)
      .slice(0, 6)

    const suggestionChips: string[] = []
    for (const term of popular.slice(0, 6)) {
      suggestionChips.push(
        `<button class="fs-suggestion-chip" data-query="${this.escapeHtml(term)}">${this.escapeHtml(term)}</button>`
      )
    }
    for (const cat of topCats) {
      suggestionChips.push(
        `<button class="fs-suggestion-chip fs-suggestion-chip-cat" data-category="${this.escapeHtml(cat.name)}">${this.escapeHtml(cat.name)}</button>`
      )
    }

    const queryText = this.lastQuery ? this.escapeHtml(this.lastQuery) : ''
    let headline: string
    if (queryText && hasFilters) {
      headline = `No matches for <strong>"${queryText}"</strong> with ${activeFilters.join(' + ')}`
    } else if (queryText) {
      headline = `No results for <strong>"${queryText}"</strong>`
    } else if (hasFilters) {
      headline = `No products match ${activeFilters.join(' + ')}`
    } else {
      headline = 'No results found'
    }

    const hint = hasFilters
      ? `Try clearing your filters, or search for something else:`
      : suggestionChips.length > 0
        ? `Try one of these instead:`
        : `Try a different search term.`

    const clearBtn = hasFilters
      ? `<button class="fs-empty-clear" type="button">Clear filters</button>`
      : ''

    const chipsHtml =
      suggestionChips.length > 0
        ? `<div class="fs-suggestions">${suggestionChips.join('')}</div>`
        : ''

    const bodyHtml = `
			<div class="fs-empty-inner">
				<svg class="fs-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
					<circle cx="11" cy="11" r="8"></circle>
					<path d="m21 21-4.35-4.35"></path>
					<path d="M8 11h6"></path>
				</svg>
				<p class="fs-empty-headline">${headline}</p>
				<p class="fs-hint">${hint}</p>
				${clearBtn}
				${chipsHtml}
			</div>
		`

    if (this.config.mode === 'dropdown') {
      const resultsEl = this.shadowRoot?.querySelector('.flaresearch-results') as HTMLElement
      const emptyEl = this.shadowRoot?.querySelector('.flaresearch-empty') as HTMLElement
      if (resultsEl) resultsEl.replaceChildren()
      if (emptyEl) {
        this.setEmptyStateContent(emptyEl, bodyHtml)
        emptyEl.style.display = 'block'
      }
    } else {
      // Fullscreen: switch to results body (so facets stay visible) and
      // populate the empty container.
      this.setBodyState('empty')
      const emptyEl = this.shadowRoot?.querySelector('.fs-empty') as HTMLElement
      if (emptyEl) {
        this.setEmptyStateContent(emptyEl, bodyHtml)
      }
    }
  }

  /**
   * Populate an empty-state container with HTML and wire up its
   * suggestion/clear-filter buttons.
   */
  private setEmptyStateContent(container: HTMLElement, bodyHtml: string): void {
    const tmp = document.createElement('div')
    // eslint-disable-next-line no-unsanitized/property -- all values escaped in showEmpty()
    tmp.innerHTML = bodyHtml
    container.replaceChildren(...Array.from(tmp.childNodes))

    // Suggestion chips: text chips fire a new search, category chips set the filter
    container.querySelectorAll('.fs-suggestion-chip').forEach((btn) => {
      btn.addEventListener('click', () => {
        const el = btn as HTMLElement
        const cat = el.dataset['category']
        const q = el.dataset['query']
        if (cat) {
          this.filterCategory = cat
          this.refreshFilterChips()
          this.rerunSearch()
        } else if (q && this.input) {
          this.input.value = q
          this.hideEmpty()
          this.handleInput(q)
        }
      })
    })

    // Clear filters from within the empty state. Same effect as the
    // sidebar Clear-all link.
    container.querySelector('.fs-empty-clear')?.addEventListener('click', () => {
      this.filterCategory = null
      this.filterBrand = null
      this.filterModelId = null
      this.filterType = null
      this.filterPriceMin = null
      this.filterPriceMax = null
      this.filterInStockOnly = false

      const inStockCheckbox = this.shadowRoot?.querySelector('.fs-filter-instock') as HTMLInputElement | null
      const priceMin = this.shadowRoot?.querySelector('.fs-filter-price-min') as HTMLInputElement | null
      const priceMax = this.shadowRoot?.querySelector('.fs-filter-price-max') as HTMLInputElement | null
      if (inStockCheckbox) inStockCheckbox.checked = false
      if (priceMin) priceMin.value = ''
      if (priceMax) priceMax.value = ''

      this.refreshFilterChips()
      this.rerunSearch()
    })
  }

  private hideEmpty(): void {
    const emptyEl = this.shadowRoot?.querySelector(
      this.config.mode === 'dropdown' ? '.flaresearch-empty' : '.fs-empty'
    ) as HTMLElement
    if (emptyEl) emptyEl.style.display = 'none'
  }

  private showInitialState(): void {
    if (this.config.mode === 'fullscreen') {
      this.setBodyState('initial')
    }
  }

  destroy(): void {
    if (this.config.mode === 'fullscreen') {
      this.closeFullscreen()
    }
    this.container?.remove()
    this.container = null
    this.shadowRoot = null
  }
}

// Global API
const FlareSearch = {
  instance: null as FlareSearchWidget | null,
  apiBaseUrl: window.location.origin,

  init(config: FlareSearchConfig): void {
    if (this.instance) {
      this.instance.destroy()
    }
    this.instance = new FlareSearchWidget(config)
    this.instance.init()
    this.apiBaseUrl = config.apiBaseUrl || window.location.origin
  },

  destroy(): void {
    if (this.instance) {
      this.instance.destroy()
      this.instance = null
    }
  },

  /**
   * Open quick view modal for a product
   * Can be called from anywhere: FlareSearch.openQuickView('product_123')
   */
  openQuickView(productId: string): void {
    if (this.instance) {
      this.instance.openQuickView(productId)
    } else {
      console.warn('FlareSearch: Widget not initialized. Call FlareSearch.init() first.')
    }
  },

  /**
   * Close quick view modal
   */
  closeQuickView(): void {
    if (this.instance) {
      this.instance.closeQuickView()
    }
  },

  /**
   * Get similar products for a product ID
   * Returns a promise with similar product data
   */
  async getSimilar(
    productId: string,
    limit: number = 8
  ): Promise<SimilarProduct[]> {
    try {
      const response = await fetch(
        `${this.apiBaseUrl}/api/search/similar/${productId}?limit=${limit}`
      )

      if (!response.ok) {
        throw new Error('Failed to fetch similar products')
      }

      const data = (await response.json()) as { similar: SimilarProduct[] }
      return data.similar
    } catch (error) {
      console.error('FlareSearch: getSimilar error', error)
      return []
    }
  },

  /**
   * Get product details for quick view
   */
  async getProduct(productId: string): Promise<ProductDetails | null> {
    try {
      const response = await fetch(
        `${this.apiBaseUrl}/api/search/product/${productId}`
      )

      if (!response.ok) {
        throw new Error('Failed to fetch product details')
      }

      const data = (await response.json()) as { product: ProductDetails }
      return data.product
    } catch (error) {
      console.error('FlareSearch: getProduct error', error)
      return null
    }
  },
}

// Export for module usage
export { FlareSearch, FlareSearchWidget }

// Attach to window for script tag usage
if (typeof window !== 'undefined') {
  ;(window as unknown as { FlareSearch: typeof FlareSearch }).FlareSearch = FlareSearch
}
